import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react"; // Import useState and useEffect
import "../styles.css";
import { rgbaToHex } from "../../../../util/canvas/canvasColorUtil";
import SimpleLexicalEditorWrapper from "../text-editor/SimpleLexicalEditor";
import generateHtmlWithStylesFromNodes from "../canvas-core/generateHtmlWithStylesFromNodes";
import CopyIcon from "../selection/CopyIcon";
import {
  getCanvasStageHeightPx,
  getMainCanvasWidthPx,
  getOffCanvasStageWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "../../utils/mainCanvasLayout";
import { getOffCanvasMainColumnBounds } from "../../utils/boundaryUtils";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const resolveCanvasHeightPx = (canvasHeight) =>
  Number(canvasHeight) > 100
    ? Number(canvasHeight)
    : getStageHeightPxFromCanvasHeight(canvasHeight);

const clampNumericValue = (value, min, max) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  let num = parseFloat(String(value));
  if (Number.isNaN(num)) {
    return null;
  }
  if (typeof min === "number" && num < min) {
    num = min;
  }
  if (typeof max === "number" && num > max) {
    num = max;
  }
  return num;
};

const createDefaultRectanglePreset = () => ({
  id: "rectangle-default-preset",
  name: "Default Rectangle",
  fill: "#FFFFFF",
  fillOpacity: 1,
  stroke: "#FF0000",
  strokeOpacity: 1,
  strokeWidth: 2,
  fontSize: 15,
  textColor: "#000000",
  fontStyle: "normal",
  textDecoration: "none",
  align: "center",
  verticalAlign: "middle",
  text: "",
  width: 50,
  height: 50,
  rotation: 0,
  maintainAspectRatio: false,
});

function RectangleEditor({
  selectedRectangle,
  updateRectangleProperties,
  onAddText,
  onDelete,
  onRemoveText,
  canvasHeight,
  mainCanvasWidthPx,
  onToggleCloneMode = null,
  isCloneMode = false,
}) {
  const DEFAULT_PRESET_STORAGE_KEY = "rectangleDefaultStylePreset";
  const DEFAULT_PRESET_ID_STORAGE_KEY = "rectangleDefaultStylePresetId";
  const EDITOR_EDGE_BUFFER_PX = 10;
  const ROTATION_FIT_EPSILON_PX = 0.5;
  const MIN_WIDTH = 10;
  const mainStageW = Math.max(
    MIN_WIDTH,
    Number(mainCanvasWidthPx) || getMainCanvasWidthPx(),
  );
  const MAX_WIDTH = mainStageW;

  const MIN_HEIGHT = 0;
  const MAX_HEIGHT = 600;

  const MIN_X = 0;
  const MAX_X = Math.max(0, mainStageW - MIN_WIDTH);

  const MIN_FONT_SIZE = 5;
  const MAX_FONT_SIZE = Math.min(
    selectedRectangle.height * 0.8, // Vertical constraint: Font height should not exceed 80% of rectangle height.
    selectedRectangle.width * 0.8, // Horizontal constraint: Font size (as an approximation of character width)
    // should not exceed 80% of rectangle width, ensuring single character fit
    // and providing horizontal breathing room for very short texts.
  );
  const MIN_Y = 0;

  const MAX_Y = 760; // or whatever largest expected height

  const MIN_ROTATION = -360;
  const MAX_ROTATION = 360;

  const MIN_STROKE_WIDTH = 1;
  const MAX_STROKE_WIDTH = 20;

  const [showTextEditor, setShowTextEditor] = useState(false); // State to control text editor visibility
  const [editingPreset, setEditingPreset] = useState(null);
  const [pendingDefaultPresetId, setPendingDefaultPresetId] = useState(null);
  const [defaultPresetIdBeforeEdit, setDefaultPresetIdBeforeEdit] =
    useState(null);
  const [pendingNewPresetId, setPendingNewPresetId] = useState(null);
  const [isStylesExpanded, setIsStylesExpanded] = useState(true);
  const [isStyleSectionExpanded, setIsStyleSectionExpanded] = useState(true);
  const [formattingUpdate, setFormattingUpdate] = useState(0); // State to trigger re-renders for formatting buttons

  // Determine if rectangle is on off-canvas by checking x position
  // Note: Negative x values are allowed for main canvas stage elements
  // For off-canvas rectangles, we need to use off-canvas stage dimensions
  // CRITICAL FIX: Check _isInOffCanvasDuringDrag flag first (for elements being dragged between canvases)
  // If flag is explicitly false, element is on main canvas (don't check x position)
  // If flag is true, element is on off-canvas
  // If flag is undefined, fall back to x position check for elements already on off-canvas
  const isOffCanvas =
    selectedRectangle?._previewTargetStage === "offCanvas"
      ? true
      : selectedRectangle?._previewTargetStage === "main"
        ? false
        : (selectedRectangle?._isDragging === true &&
            selectedRectangle?.isOffCanvas === true) ||
          (selectedRectangle?._isDragging === true &&
            selectedRectangle?._previewTargetStage !== "main" &&
            (selectedRectangle?.isOffCanvas === true ||
              selectedRectangle?._isInOffCanvasDuringDrag === true ||
              (selectedRectangle?._isInOffCanvasDuringDrag === undefined &&
                selectedRectangle?.x !== undefined &&
                selectedRectangle?.x >= mainStageW))) ||
          (selectedRectangle?._isDragging !== true &&
            (selectedRectangle?.isOffCanvas === true ||
              selectedRectangle?._isInOffCanvasDuringDrag === true ||
              (selectedRectangle?._isInOffCanvasDuringDrag === undefined &&
                selectedRectangle?.x !== undefined &&
                selectedRectangle?.x >= mainStageW)));

  // Calculate proper constraints based on canvas type
  const getStageConstraints = () => {
    const edgeInset = EDITOR_EDGE_BUFFER_PX;
    if (isOffCanvas) {
      // Off-canvas stage uses full viewport width
      const stageWidth = getOffCanvasStageWidthPx();
      const stageHeight = getCanvasStageHeightPx();
      const rectWidth = selectedRectangle?.width || MAX_WIDTH;
      return {
        minX: edgeInset,
        maxX: Math.max(edgeInset, stageWidth - rectWidth - edgeInset),
        minY: edgeInset,
        maxY: Math.max(
          edgeInset,
          stageHeight - (selectedRectangle?.height || MAX_HEIGHT) - edgeInset,
        ),
      };
    } else {
      const mainStageH = resolveCanvasHeightPx(canvasHeight);
      const rectW = selectedRectangle?.width || MIN_WIDTH;
      const rectH = selectedRectangle?.height || MIN_HEIGHT;
      // Main canvas constraints - allow negative x values
      return {
        minX: -10000, // Allow negative x values for main canvas stage elements
        maxX: Math.max(edgeInset, mainStageW - rectW - edgeInset),
        minY: edgeInset,
        maxY: Math.max(edgeInset, mainStageH - rectH - edgeInset),
      };
    }
  };

  const stageConstraints = getStageConstraints();
  const MIN_X_CONSTRAINED = stageConstraints.minX;
  const MAX_X_CONSTRAINED = stageConstraints.maxX;
  const MIN_Y_CONSTRAINED = stageConstraints.minY;
  const MAX_Y_CONSTRAINED = stageConstraints.maxY;
  const activeStageWidth = isOffCanvas ? getOffCanvasStageWidthPx() : mainStageW;
  const activeStageHeight = isOffCanvas
    ? getCanvasStageHeightPx()
    : resolveCanvasHeightPx(canvasHeight);
  const currentRectX = Number(selectedRectangle?.x) || 0;
  const currentRectY = Number(selectedRectangle?.y) || 0;
  const getRotatedBoundsTopLeft = (x, y, width, height, rotationDeg) => {
    const theta = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const corners = [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ].map((p) => ({
      x: x + p.x * cos - p.y * sin,
      y: y + p.x * sin + p.y * cos,
    }));
    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  };
  const fitsInStageWithInset = (x, y, width, height, rotationDeg) => {
    const b = getRotatedBoundsTopLeft(x, y, width, height, rotationDeg);
    if (isOffCanvas) {
      const { mainZoneStart } = getOffCanvasMainColumnBounds(activeStageWidth);
      return (
        b.minX >= EDITOR_EDGE_BUFFER_PX - ROTATION_FIT_EPSILON_PX &&
        b.maxX <=
          mainZoneStart - EDITOR_EDGE_BUFFER_PX + ROTATION_FIT_EPSILON_PX &&
        b.minY >= EDITOR_EDGE_BUFFER_PX - ROTATION_FIT_EPSILON_PX &&
        b.maxY <=
          activeStageHeight - EDITOR_EDGE_BUFFER_PX + ROTATION_FIT_EPSILON_PX
      );
    }
    return (
      b.minX >= EDITOR_EDGE_BUFFER_PX - ROTATION_FIT_EPSILON_PX &&
      b.maxX <=
        activeStageWidth - EDITOR_EDGE_BUFFER_PX + ROTATION_FIT_EPSILON_PX &&
      b.minY >= EDITOR_EDGE_BUFFER_PX - ROTATION_FIT_EPSILON_PX &&
      b.maxY <=
        activeStageHeight - EDITOR_EDGE_BUFFER_PX + ROTATION_FIT_EPSILON_PX
    );
  };
  const clampPositionForRotationInStage = (x, y, width, height, rotationDeg) => {
    const b = getRotatedBoundsTopLeft(x, y, width, height, rotationDeg);
    const minX = EDITOR_EDGE_BUFFER_PX;
    const maxX = isOffCanvas
      ? getOffCanvasMainColumnBounds(activeStageWidth).mainZoneStart -
        EDITOR_EDGE_BUFFER_PX
      : activeStageWidth - EDITOR_EDGE_BUFFER_PX;
    const minY = EDITOR_EDGE_BUFFER_PX;
    const maxY = activeStageHeight - EDITOR_EDGE_BUFFER_PX;
    let shiftX = 0;
    let shiftY = 0;

    if (b.minX < minX) {
      shiftX += minX - b.minX;
    }
    if (b.maxX > maxX) {
      shiftX -= b.maxX - maxX;
    }
    if (b.minY < minY) {
      shiftY += minY - b.minY;
    }
    if (b.maxY > maxY) {
      shiftY -= b.maxY - maxY;
    }

    return {
      x: x + shiftX,
      y: y + shiftY,
    };
  };
  const getRotationRelativeBounds = (width, height, rotationDeg) => {
    const theta = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    const corners = [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ].map((p) => ({
      x: p.x * cos - p.y * sin,
      y: p.x * sin + p.y * cos,
    }));
    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  };
  const getRotationAwarePositionBounds = () => {
    const rectWidth = Math.max(MIN_WIDTH, Number(selectedRectangle?.width) || MIN_WIDTH);
    const rectHeight = Math.max(
      MIN_HEIGHT,
      Number(selectedRectangle?.height) || MIN_HEIGHT,
    );
    const rotationDeg = Number(selectedRectangle?.rotation) || 0;
    const rel = getRotationRelativeBounds(rectWidth, rectHeight, rotationDeg);
    return {
      minX: EDITOR_EDGE_BUFFER_PX - rel.minX,
      maxX: activeStageWidth - EDITOR_EDGE_BUFFER_PX - rel.maxX,
      minY: EDITOR_EDGE_BUFFER_PX - rel.minY,
      maxY: activeStageHeight - EDITOR_EDGE_BUFFER_PX - rel.maxY,
    };
  };
  const getRotationAwareMaxWidth = (heightForCalc) => {
    const rectWidth = Math.max(MIN_WIDTH, Number(selectedRectangle?.width) || MIN_WIDTH);
    const rectHeight = Math.max(
      MIN_HEIGHT,
      Number(heightForCalc ?? selectedRectangle?.height) || MIN_HEIGHT,
    );
    const centerX = (Number(selectedRectangle?.x) || 0) + rectWidth / 2;
    const centerY = (Number(selectedRectangle?.y) || 0) + rectHeight / 2;
    const halfSpaceX = Math.max(
      0,
      Math.min(
        centerX - EDITOR_EDGE_BUFFER_PX,
        activeStageWidth - EDITOR_EDGE_BUFFER_PX - centerX,
      ),
    );
    const halfSpaceY = Math.max(
      0,
      Math.min(
        centerY - EDITOR_EDGE_BUFFER_PX,
        activeStageHeight - EDITOR_EDGE_BUFFER_PX - centerY,
      ),
    );
    const rotationDeg = Number(selectedRectangle?.rotation) || 0;
    const normalizedRotation = ((rotationDeg % 360) + 360) % 360;
    const isAxisAligned =
      Math.abs(normalizedRotation - 0) < 0.01 ||
      Math.abs(normalizedRotation - 180) < 0.01;
    // For axis-aligned rectangles, width input should grow from current x (top-left),
    // not be limited by center-based math.
    if (isAxisAligned && !isOffCanvas) {
      // Use just the buffer to achieve exactly 700 width, matching rectangle drawing behavior
      return Math.max(
        MIN_WIDTH,
        // Keep manual input aligned with transform-end clamp semantics
        // where right-edge fit is inclusive for axis-aligned rects.
        Math.min(700, activeStageWidth - EDITOR_EDGE_BUFFER_PX - currentRectX + 1), // Cap at exactly 700
      );
    }
    if (isAxisAligned && isOffCanvas) {
      const { mainZoneStart } = getOffCanvasMainColumnBounds(activeStageWidth);
      return Math.max(
        MIN_WIDTH,
        // Off-canvas transformer/end-clamp treats the right boundary as inclusive
        // for axis-aligned rect widths, so mirror that +1px here.
        Math.min(700, mainZoneStart - EDITOR_EDGE_BUFFER_PX - currentRectX + 1), // Cap at exactly 700
      );
    }
    // Rotated case: compute the true max width with fixed top-left x/y and current rotation.
    let low = MIN_WIDTH;
    let high = Math.max(activeStageWidth * 2, rectWidth);
    for (let i = 0; i < 32; i += 1) {
      const mid = (low + high) / 2;
      if (fitsInStageWithInset(currentRectX, currentRectY, mid, rectHeight, rotationDeg)) {
        low = mid;
      } else {
        high = mid;
      }
    }
    return Math.max(MIN_WIDTH, low);
  };
  const getRotationAwareMaxHeight = (widthForCalc) => {
    const rectWidth = Math.max(
      MIN_WIDTH,
      Number(widthForCalc ?? selectedRectangle?.width) || MIN_WIDTH,
    );
    const rectHeight = Math.max(MIN_HEIGHT, Number(selectedRectangle?.height) || MIN_HEIGHT);
    const centerX = (Number(selectedRectangle?.x) || 0) + rectWidth / 2;
    const centerY = (Number(selectedRectangle?.y) || 0) + rectHeight / 2;
    const halfSpaceX = Math.max(
      0,
      Math.min(
        centerX - EDITOR_EDGE_BUFFER_PX,
        activeStageWidth - EDITOR_EDGE_BUFFER_PX - centerX,
      ),
    );
    const halfSpaceY = Math.max(
      0,
      Math.min(
        centerY - EDITOR_EDGE_BUFFER_PX,
        activeStageHeight - EDITOR_EDGE_BUFFER_PX - centerY,
      ),
    );
    const rotationDeg = Number(selectedRectangle?.rotation) || 0;
    const normalizedRotation = ((rotationDeg % 360) + 360) % 360;
    const isAxisAligned =
      Math.abs(normalizedRotation - 0) < 0.01 ||
      Math.abs(normalizedRotation - 180) < 0.01;
    if (isAxisAligned) {
      // Apply the same position optimization logic as RectangleElement's clampNodePositionInsideStageByClientRect
      // RectangleElement first optimizes the position, then calculates available space
      
      const edgeInset = EDITOR_EDGE_BUFFER_PX;
      
      // RectangleElement optimizes position to maximize available space
      // For axis-aligned rectangles, it moves them to the optimal Y position first
      // The optimal Y position is the minimum allowed (edgeInset) to maximize height
      
      const optimalY = edgeInset; // RectangleElement moves rectangles to minY to maximize height
      
      console.log("DEBUG: getRotationAwareMaxHeight position optimization", {
        currentRectY: currentRectY,
        optimalY: optimalY,
        positionOptimized: currentRectY !== optimalY,
        edgeInset: edgeInset
      });
      
      // Now calculate max height using the optimized position (same as RectangleElement)
      let maxHeight = activeStageHeight - edgeInset - optimalY;
      
      console.log("DEBUG: getRotationAwareMaxHeight axis-aligned with optimized position", {
        currentRectY: currentRectY,
        optimalY: optimalY,
        maxHeight: maxHeight,
        activeStageHeight: activeStageHeight,
        edgeInset: edgeInset,
        calculation: `activeStageHeight(${activeStageHeight}) - edgeInset(${edgeInset}) - optimalY(${optimalY}) = ${maxHeight}`
      });
      
      return Math.max(MIN_HEIGHT, maxHeight);
    }
    // Rotated case: compute true max height with fixed top-left x/y and rotation.
    let low = MIN_HEIGHT;
    let high = Math.max(activeStageHeight * 2, rectHeight || MIN_HEIGHT);
    for (let i = 0; i < 32; i += 1) {
      const mid = (low + high) / 2;
      if (fitsInStageWithInset(currentRectX, currentRectY, rectWidth, mid, rotationDeg)) {
        low = mid;
      } else {
        high = mid;
      }
    }
    return Math.max(MIN_HEIGHT, low);
  };
  const dynamicMaxWidth = Math.max(
    MIN_WIDTH,
    getRotationAwareMaxWidth(),
  );
  const dynamicMaxHeight = Math.max(
    MIN_HEIGHT,
    getRotationAwareMaxHeight(),
  );
  // Avoid fractional precision causing a 1px lower cap in number input
  // (e.g. computed 815.999999 while transform result displays 816).
  const editableMaxHeightForInput = Math.max(
    MIN_HEIGHT,
    Math.ceil(dynamicMaxHeight - 1e-6),
  );
  // Width input must respect current boundary fit (position + rotation aware)
  // so rectangles cannot extend outside stage bounds.
  // Allow width to be increased up to dynamicMaxWidth
  const currentRectWidth = Number(selectedRectangle?.width) || MIN_WIDTH;
  const editableMaxWidth = Math.max(
    MIN_WIDTH,
    dynamicMaxWidth, // Allow increasing up to the dynamic maximum
  );
  // Keep number-input max aligned with transform-end max without overshooting.
  // Use floor with epsilon so 815.999999 becomes 816, but 816.499 stays 816.
  const editableMaxWidthForInput = Math.max(
    MIN_WIDTH,
    Math.floor(editableMaxWidth + 1e-6),
  );

  // DEBUG: Log width constraint calculations
  console.log("DEBUG: Width constraint calculation:", {
    MIN_WIDTH: MIN_WIDTH,
    dynamicMaxWidth: dynamicMaxWidth,
    currentRectWidth: currentRectWidth,
    minDynamicMaxCurrent: Math.min(dynamicMaxWidth, currentRectWidth),
    editableMaxWidth: editableMaxWidth,
    editableMaxWidthForInput: editableMaxWidthForInput,
    currentRectX: currentRectX,
    isOffCanvas: isOffCanvas,
    rectangleRotation: Number(selectedRectangle?.rotation) || 0
  });

  // Local state for input values to avoid immediate updates
  const [localInputValues, setLocalInputValues] = useState({
    x:
      selectedRectangle?.x !== undefined && selectedRectangle?.x !== null
        ? Math.round(selectedRectangle.x)
        : "",
    y:
      selectedRectangle?.y !== undefined && selectedRectangle?.y !== null
        ? Math.round(selectedRectangle.y)
        : "",
    width: selectedRectangle?.width ? selectedRectangle.width.toFixed(2) : "",
    height: selectedRectangle?.height
      ? selectedRectangle.height.toFixed(2)
      : "",
    rotation:
      selectedRectangle?.rotation !== undefined
        ? Math.round(selectedRectangle.rotation)
        : 0,
    strokeWidth: selectedRectangle?.strokeWidth
      ? Math.round(selectedRectangle.strokeWidth)
      : "",
    fillOpacity:
      selectedRectangle?.fillOpacity !== undefined
        ? Math.round(selectedRectangle.fillOpacity * 100)
        : "",
    strokeOpacity:
      selectedRectangle?.strokeOpacity !== undefined
        ? Math.round(selectedRectangle.strokeOpacity * 100)
        : "",
    textColorHex: selectedRectangle?.textColor
      ? selectedRectangle.textColor.toUpperCase().replace("#", "")
      : "000000",
    strokeColorHex: selectedRectangle?.stroke
      ? rgbaToHex(selectedRectangle.stroke).toUpperCase().replace("#", "")
      : "000000",
  });

  // Aspect ratio state
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(
    selectedRectangle?.maintainAspectRatio || false,
  );
  const [isEditingRotation, setIsEditingRotation] = useState(false);
  const rotationIntervalRef = useRef(null);
  const rotationStopHandlerRef = useRef(null);
  const rotationDraftRef = useRef(null);
  const lastRectangleIdRef = useRef(null);
  const isProgrammaticallyChangingFontSizeRef = useRef(false);
  const selectionUpdateListenersRef = useRef({ enabled: true });
  const isRotationDebugEnabled = (() => {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debugRectEditorRotation") === "1"
      );
    } catch {
      return false;
    }
  })();

  // Helper functions for rotation arrow holding
  const normalizeRotationValue = (value) => {
    let numeric =
      typeof value === "number"
        ? value
        : value === "" || value === null || value === undefined
          ? 0
          : parseInt(value, 10);
    if (Number.isNaN(numeric)) {
      numeric = 0;
    }
    let normalizedValue = numeric % 360;
    if (normalizedValue < 0) {
      normalizedValue += 360;
    }
    return normalizedValue;
  };

  const getCenterForTopLeftRotation = (x, y, width, height, rotationDeg) => {
    const theta = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    return {
      x: x + (width / 2) * cos - (height / 2) * sin,
      y: y + (width / 2) * sin + (height / 2) * cos,
    };
  };

  const getTopLeftForCenterRotation = (
    centerX,
    centerY,
    width,
    height,
    rotationDeg,
  ) => {
    const theta = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    return {
      x: centerX - ((width / 2) * cos - (height / 2) * sin),
      y: centerY - ((width / 2) * sin + (height / 2) * cos),
    };
  };

  const finalizeRotationChange = (valueOverride = null) => {
    const sourceValue =
      valueOverride !== null && valueOverride !== undefined
        ? valueOverride
        : rotationDraftRef.current !== null
          ? rotationDraftRef.current
          : localInputValues.rotation;

    const normalizedValue = normalizeRotationValue(sourceValue);
    rotationDraftRef.current = null;

    setLocalInputValues((prev) => ({
      ...prev,
      rotation: normalizedValue,
    }));

    const width = Math.max(MIN_WIDTH, Number(selectedRectangle?.width) || MIN_WIDTH);
    const height = Math.max(
      MIN_HEIGHT,
      Number(selectedRectangle?.height) || MIN_HEIGHT,
    );
    const currentX = Number(selectedRectangle?.x) || 0;
    const currentY = Number(selectedRectangle?.y) || 0;
    const previousRotation = normalizeRotationValue(selectedRectangle?.rotation ?? 0);

    const currentCenter = getCenterForTopLeftRotation(
      currentX,
      currentY,
      width,
      height,
      previousRotation,
    );
    const requestedTopLeft = getTopLeftForCenterRotation(
      currentCenter.x,
      currentCenter.y,
      width,
      height,
      normalizedValue,
    );

    const requestedFitsAtCenterLockedPosition = fitsInStageWithInset(
      requestedTopLeft.x,
      requestedTopLeft.y,
      width,
      height,
      normalizedValue,
    );

    if (isRotationDebugEnabled) {
    }

    if (requestedFitsAtCenterLockedPosition) {
      updateRectangleProperties({
        rotation: normalizedValue,
        x: requestedTopLeft.x,
        y: requestedTopLeft.y,
      });
      return;
    }

    // Match transform-end behavior: if requested rotation doesn't fit at the
    // center-locked top-left, try clamping x/y for that same rotation before reverting.
    const clampedRequested = clampPositionForRotationInStage(
      requestedTopLeft.x,
      requestedTopLeft.y,
      width,
      height,
      normalizedValue,
    );
    const requestedFitsAfterPositionClamp = fitsInStageWithInset(
      clampedRequested.x,
      clampedRequested.y,
      width,
      height,
      normalizedValue,
    );
    if (requestedFitsAfterPositionClamp) {
      updateRectangleProperties({
        rotation: normalizedValue,
        x: clampedRequested.x,
        y: clampedRequested.y,
      });
      return;
    }

    if (isRotationDebugEnabled) {
    }

    setLocalInputValues((prev) => ({
      ...prev,
      rotation: previousRotation,
    }));

    updateRectangleProperties({
      rotation: previousRotation,
      x: currentX,
      y: currentY,
    });
  };

  const startRotationChange = (direction) => {
    if (rotationIntervalRef.current) return;

    const baseRotation =
      rotationDraftRef.current !== null
        ? rotationDraftRef.current
        : typeof localInputValues.rotation === "number"
          ? localInputValues.rotation
          : normalizeRotationValue(
              localInputValues.rotation ?? selectedRectangle.rotation ?? 0,
            );

    rotationDraftRef.current = baseRotation;

    const step = () => {
      const current = rotationDraftRef.current ?? 0;
      let nextRotation = current + direction;
      nextRotation = normalizeRotationValue(nextRotation);
      rotationDraftRef.current = nextRotation;
      setLocalInputValues((prev) => ({
        ...prev,
        rotation: nextRotation,
      }));
    };

    step();

    rotationIntervalRef.current = setInterval(() => {
      step();
    }, 100);

    const stopHandler = () => {
      stopRotationChange(true);
    };
    rotationStopHandlerRef.current = stopHandler;
    const stopEvents = [
      "pointerup",
      "pointercancel",
      "mouseup",
      "mouseleave",
      "touchend",
      "touchcancel",
      "blur",
    ];
    stopEvents.forEach((eventName) =>
      window.addEventListener(eventName, stopHandler),
    );
  };

  const stopRotationChange = (shouldFinalize = false) => {
    if (rotationIntervalRef.current) {
      clearInterval(rotationIntervalRef.current);
      rotationIntervalRef.current = null;
    }
    if (rotationStopHandlerRef.current) {
      const stopEvents = [
        "pointerup",
        "pointercancel",
        "mouseup",
        "mouseleave",
        "touchend",
        "touchcancel",
        "blur",
      ];
      stopEvents.forEach((eventName) =>
        window.removeEventListener(eventName, rotationStopHandlerRef.current),
      );
      rotationStopHandlerRef.current = null;
    }
    if (shouldFinalize) {
      finalizeRotationChange();
    } else {
      rotationDraftRef.current = null;
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      stopRotationChange(false);
    };
  }, []);

  // Update local state when selectedRectangle changes
  useEffect(() => {
    if (selectedRectangle) {
      const isNewRectangle =
        lastRectangleIdRef.current !== selectedRectangle.id;
      lastRectangleIdRef.current = selectedRectangle.id;

      // Recalculate constraints when rectangle changes (dimensions might have changed)
      const constraints = getStageConstraints();

      // Reset changingText flag when selecting a new rectangle to ensure clean state
      if (isNewRectangle && showTextEditor) {
        // If switching to a new rectangle while text editor is open, close it first
        setShowTextEditor(false);
        updateRectangleProperties({ changingText: false });
      }

      // Check if there's currently a text selection in the editor
      // If so, delay resetting textColorHex to allow selection color detection to run first
      const sel = window.getSelection && window.getSelection();
      const hasActiveSelection =
        sel && sel.rangeCount > 0 && !sel.isCollapsed
          ? sel.anchorNode?.parentElement?.closest('[contenteditable="true"]')
          : null;

      // Recalculate constraints for this rectangle
      // Note: Negative x values are allowed for main canvas stage elements
      // CRITICAL FIX: Check _isInOffCanvasDuringDrag flag first (for elements being dragged between canvases)
      // If flag is explicitly false, element is on main canvas (don't check x position)
      // If flag is true, element is on off-canvas
      // If flag is undefined, fall back to x position check for elements already on off-canvas
      const currentIsOffCanvas =
        selectedRectangle?._previewTargetStage === "offCanvas"
          ? true
          : selectedRectangle?._previewTargetStage === "main"
            ? false
            : (selectedRectangle?._isDragging === true &&
                selectedRectangle?.isOffCanvas === true) ||
              (selectedRectangle?._isDragging === true &&
                selectedRectangle?._previewTargetStage !== "main" &&
                (selectedRectangle?.isOffCanvas === true ||
                  selectedRectangle?._isInOffCanvasDuringDrag === true ||
                  (selectedRectangle?._isInOffCanvasDuringDrag === undefined &&
                    selectedRectangle?.x !== undefined &&
                    selectedRectangle?.x >= mainStageW))) ||
              (selectedRectangle?._isDragging !== true &&
                (selectedRectangle?.isOffCanvas === true ||
                  selectedRectangle?._isInOffCanvasDuringDrag === true ||
                  (selectedRectangle?._isInOffCanvasDuringDrag === undefined &&
                    selectedRectangle?.x !== undefined &&
                    selectedRectangle?.x >= mainStageW)));
      const mainStageHSync = resolveCanvasHeightPx(canvasHeight);
      const currentConstraints = currentIsOffCanvas
        ? {
            minX: 0,
            maxX: Math.max(
              0,
              getOffCanvasStageWidthPx() - (selectedRectangle?.width || MAX_WIDTH),
            ),
            minY: 0,
            maxY: Math.max(
              0,
              getCanvasStageHeightPx() -
                (selectedRectangle?.height || MAX_HEIGHT),
            ),
          }
        : {
            minX: -10000, // Allow negative x values for main canvas stage elements
            maxX: Math.max(
              0,
              mainStageW - (selectedRectangle?.width || MIN_WIDTH),
            ),
            minY: 0,
            maxY: Math.max(0, mainStageHSync),
          };

      setLocalInputValues((prev) => {
        const updates = {
          // Keep editor position inputs in sync with the real selected rectangle position.
          // Do not clamp here based on width/height, otherwise width edits (especially around 90deg)
          // can unintentionally rewrite x/y in the UI.
          x:
            selectedRectangle.x !== undefined && selectedRectangle.x !== null
              ? Math.round(selectedRectangle.x)
              : "",
          y:
            selectedRectangle.y !== undefined && selectedRectangle.y !== null
              ? Math.round(selectedRectangle.y)
              : "",
          width: selectedRectangle.width
            ? selectedRectangle.width.toFixed(2)
            : "",
          height: selectedRectangle.height
            ? selectedRectangle.height.toFixed(2)
            : "",
          rotation:
            selectedRectangle.rotation !== undefined
              ? Math.round(selectedRectangle.rotation)
              : 0,
          strokeWidth: selectedRectangle.strokeWidth
            ? Math.round(selectedRectangle.strokeWidth)
            : "",
          fillOpacity:
            selectedRectangle.fillOpacity !== undefined
              ? Math.round(selectedRectangle.fillOpacity * 100)
              : "",
          strokeOpacity:
            selectedRectangle.strokeOpacity !== undefined
              ? Math.round(selectedRectangle.strokeOpacity * 100)
              : "",
          strokeColorHex: selectedRectangle.stroke
            ? rgbaToHex(selectedRectangle.stroke).toUpperCase().replace("#", "")
            : "000000",
        };

        // Only reset textColorHex if:
        // 1. This is a new rectangle (different ID), OR
        // 2. There's no active selection in the editor
        // Otherwise, preserve the current value to allow selection color detection to update it
        if (isNewRectangle || !hasActiveSelection) {
          const newColorHex = selectedRectangle.textColor
            ? selectedRectangle.textColor.toUpperCase().replace("#", "")
            : "000000";
          updates.textColorHex = newColorHex;
        } else {
          // Keep current value or fall back to rectangle's color if not set
          const preservedColor =
            prev.textColorHex ||
            (selectedRectangle.textColor
              ? selectedRectangle.textColor.toUpperCase().replace("#", "")
              : "000000");
          updates.textColorHex = preservedColor;
        }

        return { ...prev, ...updates };
      });
      setMaintainAspectRatio(selectedRectangle.maintainAspectRatio || false);
    }
  }, [
    selectedRectangle,
    selectedRectangle?._isInOffCanvasDuringDrag,
    selectedRectangle?.x,
    selectedRectangle?.y,
    selectedRectangle?.width,
    selectedRectangle?.height,
    selectedRectangle?.rotation,
  ]);

  const x = Number.isFinite(selectedRectangle.x)
    ? Math.round(selectedRectangle.x)
    : 0;
  const y = Number.isFinite(selectedRectangle.y)
    ? Math.round(selectedRectangle.y)
    : 0;
  const width = Number.isFinite(selectedRectangle.width)
    ? Math.round(selectedRectangle.width)
    : 100;
  const height = Number.isFinite(selectedRectangle.height)
    ? Math.round(selectedRectangle.height)
    : 100;
  const text = selectedRectangle.text || "";
  const fill =
    selectedRectangle.fill || selectedRectangle.fillColor || "#000000";
  const fillOpacity = selectedRectangle.fillOpacity ?? 1;
  const rotation = Number.isFinite(selectedRectangle.rotation)
    ? selectedRectangle.rotation
    : 0;
  const strokeColor = selectedRectangle.stroke || "#000000";
  const strokeWidth = Number.isFinite(selectedRectangle.strokeWidth)
    ? selectedRectangle.strokeWidth
    : 1;
  // --- NEW: Stroke Opacity ---
  const strokeOpacity = selectedRectangle.strokeOpacity ?? 1;
  const [stylePresets, setStylePresets] = useState(() => {
    const savedPresets = localStorage.getItem("rectangleStylePresets");
    if (savedPresets) {
      const parsed = JSON.parse(savedPresets);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
    return [createDefaultRectanglePreset()];
  });
  const [defaultPresetId, setDefaultPresetId] = useState(() =>
    localStorage.getItem(DEFAULT_PRESET_ID_STORAGE_KEY),
  );

  // Text properties from selectedRectangle, with fallbacks
  const textContent = selectedRectangle.text || "";
  const fontSize = selectedRectangle.fontSize || 15;
  const textColor = selectedRectangle.textColor || "#000000";
  const fontStyle = selectedRectangle.fontStyle || "normal"; // 'bold', 'italic', 'bold italic'
  const textDecoration = selectedRectangle.textDecoration || "none"; // 'underline', 'line-through'
  const align = selectedRectangle.align || "center";
  const verticalAlign = selectedRectangle.verticalAlign || "middle";

  // Initialize font size input value after fontSize is declared
  const [fontSizeInputValue, setFontSizeInputValue] = useState(fontSize);

  // Update font size input value when global font size changes
  useEffect(() => {
    setFontSizeInputValue(fontSize);
  }, [fontSize]);

  // Helper function to get font size from entire editor content (when no selection)
  const getEditorFontSizeFromDOM = () => {
    try {
      const editorElement = document.querySelector('[contenteditable="true"]');
      if (!editorElement) return null;

      // Get all text nodes in the editor
      const walker = document.createTreeWalker(
        editorElement,
        NodeFilter.SHOW_TEXT,
        null,
      );

      const fontSizes = new Set();
      let node = walker.nextNode();

      // Collect font sizes from all text nodes
      while (node) {
        let element = node.parentElement;
        while (element && element !== editorElement) {
          // Check inline style first
          const inlineStyle = element.getAttribute("style");
          if (inlineStyle) {
            const sizeMatch = inlineStyle.match(/font-size:\s*([^;]+)/);
            if (sizeMatch && sizeMatch[1]) {
              const sizeStr = sizeMatch[1].trim();
              const pxMatch = sizeStr.match(/(\d+(?:\.\d+)?)px/);
              if (pxMatch) {
                fontSizes.add(parseInt(pxMatch[1], 10));
                break; // Found inline style, no need to go up further
              }
            }
          }

          // Check computed style
          const computedStyle = window.getComputedStyle(element);
          const fontSize = computedStyle.fontSize;
          if (fontSize) {
            const pxMatch = fontSize.match(/(\d+(?:\.\d+)?)px/);
            if (pxMatch) {
              fontSizes.add(parseInt(pxMatch[1], 10));
            }
          }

          element = element.parentElement;
        }
        node = walker.nextNode();
      }

      // Return the most common font size, or max if multiple
      if (fontSizes.size > 0) {
        const sizes = Array.from(fontSizes);
        return Math.max(...sizes); // Return max font size found
      }

      // Fallback: check editor element's computed style
      const editorStyle = window.getComputedStyle(editorElement);
      const editorFontSize = editorStyle.fontSize;
      if (editorFontSize) {
        const pxMatch = editorFontSize.match(/(\d+(?:\.\d+)?)px/);
        if (pxMatch) {
          return parseInt(pxMatch[1], 10);
        }
      }

      return null;
    } catch (e) {
      console.error("Error getting font size from editor DOM:", e);
      return null;
    }
  };

  // Detect font size from editor content when text editor opens
  useEffect(() => {
    if (!showTextEditor) return;

    // Wait a bit for the editor to be rendered
    const timeoutId = setTimeout(() => {
      // Try to get font size from Lexical first
      const fontInfo = window.lexicalSelectedFontInfo;
      if (fontInfo && Number.isFinite(fontInfo.max)) {
        setFontSizeInputValue(fontInfo.max);
        return;
      }

      // Fallback: get from DOM
      const editorFontSize = getEditorFontSizeFromDOM();
      if (Number.isFinite(editorFontSize)) {
        setFontSizeInputValue(editorFontSize);
        return;
      }

      // Final fallback: use fontSize prop
      if (Number.isFinite(fontSize)) {
        setFontSizeInputValue(fontSize);
      }
    }, 100); // Small delay to ensure editor is rendered

    return () => clearTimeout(timeoutId);
  }, [showTextEditor, fontSize]);

  // Helper function to get font size from DOM (moved outside useEffect for accessibility)
  const getSelectedFontSizeFromDOM = () => {
    try {
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;

      const range = sel.getRangeAt(0);
      const editorElement = document.querySelector('[contenteditable="true"]');
      if (!editorElement) return null;

      // Check if selection is within the editor
      if (!editorElement.contains(range.commonAncestorContainer)) return null;

      // Get all text nodes in the range
      const walker = document.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            return range.intersectsNode(node)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        },
      );

      const fontSizes = new Set();
      let node = walker.nextNode();

      // Collect font sizes from all selected text nodes
      while (node) {
        let element = node.parentElement;
        while (element && element !== editorElement) {
          // Check inline style first
          const inlineStyle = element.getAttribute("style");
          if (inlineStyle) {
            const sizeMatch = inlineStyle.match(/font-size:\s*([^;]+)/);
            if (sizeMatch && sizeMatch[1]) {
              const sizeStr = sizeMatch[1].trim();
              const pxMatch = sizeStr.match(/(\d+(?:\.\d+)?)px/);
              if (pxMatch) {
                fontSizes.add(parseInt(pxMatch[1], 10));
                break; // Found inline style, no need to go up further
              }
            }
          }

          // Check computed style
          const computedStyle = window.getComputedStyle(element);
          const fontSize = computedStyle.fontSize;
          if (fontSize) {
            const pxMatch = fontSize.match(/(\d+(?:\.\d+)?)px/);
            if (pxMatch) {
              fontSizes.add(parseInt(pxMatch[1], 10));
            }
          }

          element = element.parentElement;
        }
        node = walker.nextNode();
      }

      // Return the most common font size, or max if multiple
      if (fontSizes.size > 0) {
        const sizes = Array.from(fontSizes);
        return Math.max(...sizes); // Return max font size found
      }

      // Fallback: check anchor node
      let anchorNode = sel.anchorNode;
      if (anchorNode) {
        if (anchorNode.nodeType === 3) anchorNode = anchorNode.parentElement;
        if (anchorNode instanceof Element) {
          const withinEditor = anchorNode.closest('[contenteditable="true"]');
          if (withinEditor) {
            const style = window.getComputedStyle(anchorNode);
            const px = parseInt(style.fontSize, 10);
            if (Number.isFinite(px)) return px;
          }
        }
      }

      return null;
    } catch (e) {
      console.error("Error getting font size from DOM:", e);
      return null;
    }
  };

  // Autofill font size input with selected text's font size when selection changes
  useEffect(() => {
    const updateFromSelection = () => {
      // Early return if listeners are disabled or we're programmatically changing
      if (!selectionUpdateListenersRef.current.enabled) return;
      if (isProgrammaticallyChangingFontSizeRef.current) return;

      const inputIsFocused =
        document.activeElement &&
        document.activeElement.getAttribute &&
        (document.activeElement.getAttribute("data-font-size-input") ||
          document.activeElement.getAttribute("data-color-input"));
      if (inputIsFocused) return;

      // Check if there's actually a selection in the editor
      const sel = window.getSelection && window.getSelection();
      const hasSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;
      if (!hasSelection) {
        // No selection - don't update when programmatically changing
        // This prevents interference during arrow key changes
        if (isProgrammaticallyChangingFontSizeRef.current) return;

        // Try to get font size from entire editor content
        const editorFontSize = getEditorFontSizeFromDOM();
        if (Number.isFinite(editorFontSize)) {
          setFontSizeInputValue(editorFontSize);
          return;
        }
        // If we can't detect from editor, keep current value
        return;
      }

      // Check if selection is within the editor
      const editorElement = document.querySelector('[contenteditable="true"]');
      if (!editorElement) return;

      const range = sel.getRangeAt(0);
      if (!editorElement.contains(range.commonAncestorContainer)) return;

      // Prefer Lexical computed font info when available
      const fontInfo = window.lexicalSelectedFontInfo;
      if (fontInfo) {
        // If mixed, try to get from DOM first before clearing
        if (fontInfo.isMixed) {
          const domSize = getSelectedFontSizeFromDOM();
          if (Number.isFinite(domSize)) {
            setFontSizeInputValue(domSize);
            return;
          }
          // Only clear if we can't detect from DOM either
          setFontSizeInputValue("");
          return;
        }
        if (Number.isFinite(fontInfo.max)) {
          setFontSizeInputValue(fontInfo.max);
          return;
        }
      }

      // Fallback: DOM-computed size (this is more reliable)
      const domSize = getSelectedFontSizeFromDOM();
      if (Number.isFinite(domSize)) {
        setFontSizeInputValue(domSize);
        return;
      }

      // Fallback to previous parsing logic when selection info is provided
      if (window.selectedTextInfo && window.selectedTextInfo.text?.length > 0) {
        const parsed = getCurrentFontSize();
        if (Number.isFinite(parsed)) {
          setFontSizeInputValue(parsed);
          return;
        }
      }

      // If we have a selection but no font info, try to get from computed style
      try {
        const range = sel.getRangeAt(0);
        const container = range.commonAncestorContainer;
        let element =
          container.nodeType === Node.TEXT_NODE
            ? container.parentElement
            : container;
        const editorElement = element?.closest('[contenteditable="true"]');
        if (editorElement && element) {
          const computedStyle = window.getComputedStyle(element);
          const fontSizePx = computedStyle.fontSize;
          if (fontSizePx) {
            const match = fontSizePx.match(/(\d+(?:\.\d+)?)px/);
            if (match) {
              const size = parseInt(match[1], 10);
              if (Number.isFinite(size)) {
                setFontSizeInputValue(size);
                return;
              }
            }
          }
        }
      } catch (e) {
        // Ignore errors
      }
    };

    // Listen for selection changes
    document.addEventListener("selectionchange", updateFromSelection);
    window.addEventListener("lexical-selection-change", updateFromSelection);

    // Also listen for Lexical editor updates to catch formatting changes
    window.addEventListener("lexical-editor-update", updateFromSelection);

    // Poll for font size changes when editor is active (fallback)
    const intervalId = setInterval(() => {
      // Don't poll if we're programmatically changing font size
      if (isProgrammaticallyChangingFontSizeRef.current) return;

      // Check if any input is focused
      const activeElement = document.activeElement;
      const inputIsFocused =
        activeElement &&
        activeElement.getAttribute &&
        (activeElement.getAttribute("data-font-size-input") ||
          activeElement.getAttribute("data-color-input"));

      // Only update if input is not focused and there's a selection
      if (!inputIsFocused) {
        const sel = window.getSelection && window.getSelection();
        const hasSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;
        const editorElement = document.querySelector(
          '[contenteditable="true"]',
        );

        // Check if selection is within the editor
        if (hasSelection && editorElement) {
          const range = sel.getRangeAt(0);
          if (editorElement.contains(range.commonAncestorContainer)) {
            updateFromSelection();
          }
        }
      }
    }, 50); // Check every 50ms for more responsive updates

    return () => {
      document.removeEventListener("selectionchange", updateFromSelection);
      window.removeEventListener(
        "lexical-selection-change",
        updateFromSelection,
      );
      window.removeEventListener("lexical-editor-update", updateFromSelection);
      clearInterval(intervalId);
    };
  }, [fontSize, formattingUpdate]);

  // Update color hex input when text selection changes
  useEffect(() => {
    const getSelectedTextColorFromDOM = () => {
      try {
        const sel = window.getSelection && window.getSelection();
        if (!sel || sel.rangeCount === 0) {
          return null;
        }

        // Try to get color from the actual styled element
        const range = sel.getRangeAt(0);
        if (!range) {
          return null;
        }

        // Get the common ancestor container
        const container = range.commonAncestorContainer;
        let element =
          container.nodeType === Node.TEXT_NODE
            ? container.parentElement
            : container;

        // Walk up to find an element with a style attribute
        while (element && element.nodeType === Node.ELEMENT_NODE) {
          // Check if element is within the editor
          const withinEditor = element.closest('[contenteditable="true"]');
          if (!withinEditor) break;

          // Check inline style attribute first
          const inlineStyle = element.getAttribute("style");
          if (inlineStyle) {
            const colorMatch = inlineStyle.match(/color:\s*([^;]+)/);
            if (colorMatch && colorMatch[1]) {
              const color = colorMatch[1].trim();

              return color;
            }
          }

          element = element.parentElement;
        }

        // Fallback to computed style on the selection's container
        let node = sel.anchorNode;
        if (!node) {
          return null;
        }
        if (node.nodeType === 3) node = node.parentElement;
        if (!(node instanceof Element)) {
          return null;
        }
        const withinEditor = node.closest('[contenteditable="true"]');
        if (!withinEditor) {
          return null;
        }
        const style = window.getComputedStyle(node);
        const color = style.color;
        // Return the color even if it's black - black is a valid color
        // The issue was that we were returning null for black, which caused it to fall back to default
        if (color) {
          return color;
        }
        return null;
      } catch (error) {
        return null;
      }
    };

    const updateColorFromSelection = () => {
      const inputIsFocused =
        document.activeElement &&
        document.activeElement.getAttribute &&
        document.activeElement.getAttribute("data-color-input");

      // Check if there's actually a selection
      const sel = window.getSelection && window.getSelection();
      const hasSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;

      if (inputIsFocused) {
        return;
      }

      // If there's no selection, don't update - preserve current value
      if (!hasSelection) {
        return;
      }

      // Try to get color from Lexical editor if available
      let selectedColor = null;

      // Prefer Lexical selection color info if available (parsed from node styles)
      // This is the most accurate source as it comes directly from the selected nodes
      if (
        window.lexicalSelectedColorInfo &&
        window.lexicalSelectedColorInfo.color
      ) {
        selectedColor = window.lexicalSelectedColorInfo.color;
      } else if (window.getLexicalFormattingState) {
        // Fallback to Lexical formatting state API - but be careful with defaults
        try {
          const formatting = window.getLexicalFormattingState();
          if (formatting && formatting.textColor) {
            const formatColor = formatting.textColor;
            // Don't use #000000 from formatting state if lexicalSelectedColorInfo is not set
            // This is likely a default/fallback value, not the actual selected color
            // Only trust formatting state if it's not black, or wait for lexicalSelectedColorInfo
            if (formatColor !== "#000000" && formatColor !== "rgb(0, 0, 0)") {
              selectedColor = formatColor;
            } else {
              // Don't set selectedColor, let it fall through to DOM detection
            }
          }
        } catch (e) {
          // Fallback to DOM
        }
      }

      // Fallback: DOM-computed color (only if Lexical doesn't provide it)
      if (!selectedColor) {
        selectedColor = getSelectedTextColorFromDOM();
      }

      // Only update if we have a valid selected color
      if (selectedColor) {
        // Convert to hex if needed
        let hexColor = selectedColor;
        if (selectedColor.startsWith("rgb")) {
          // Convert rgb/rgba to hex
          const rgbMatch = selectedColor.match(/\d+/g);
          if (rgbMatch && rgbMatch.length >= 3) {
            const r = parseInt(rgbMatch[0], 10);
            const g = parseInt(rgbMatch[1], 10);
            const b = parseInt(rgbMatch[2], 10);
            hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
              .toString(16)
              .slice(1)
              .toUpperCase()}`;
          } else {
          }
        } else if (!selectedColor.startsWith("#")) {
          // If it's a named color or other format, try to get computed style
          try {
            const div = document.createElement("div");
            div.style.color = selectedColor;
            document.body.appendChild(div);
            const computed = window.getComputedStyle(div).color;
            document.body.removeChild(div);
            const rgbMatch = computed.match(/\d+/g);
            if (rgbMatch && rgbMatch.length >= 3) {
              const r = parseInt(rgbMatch[0], 10);
              const g = parseInt(rgbMatch[1], 10);
              const b = parseInt(rgbMatch[2], 10);
              hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
                .toString(16)
                .slice(1)
                .toUpperCase()}`;
            }
          } catch (e) {
            // Fallback to default
            hexColor = textColor;
          }
        }

        // Update local state with hex (without #)
        const hexWithoutHash = hexColor.toUpperCase().replace("#", "");
        setLocalInputValues((prev) => {
          const newValues = {
            ...prev,
            textColorHex: hexWithoutHash,
          };
          return newValues;
        });
      } else {
        // No selected color found, but we have a selection
        // Don't update - preserve current value to avoid resetting to default
      }
    };

    document.addEventListener("selectionchange", updateColorFromSelection);
    window.addEventListener(
      "lexical-selection-change",
      updateColorFromSelection,
    );
    return () => {
      document.removeEventListener("selectionchange", updateColorFromSelection);
      window.removeEventListener(
        "lexical-selection-change",
        updateColorFromSelection,
      );
    };
  }, [textColor, formattingUpdate]);

  // Function to get current font size for display
  const getCurrentFontSize = () => {
    // If we're programmatically changing, don't read from Lexical/DOM - use input value
    if (isProgrammaticallyChangingFontSizeRef.current) {
      return Number.isFinite(fontSizeInputValue)
        ? fontSizeInputValue
        : fontSize;
    }

    // If the input is focused, use the input value to prevent losing focus
    if (
      document.activeElement &&
      document.activeElement.type === "number" &&
      document.activeElement.getAttribute("data-font-size-input")
    ) {
      return fontSizeInputValue;
    }

    // Prefer Lexical selection info (only if not programmatically changing)
    const fontInfo = window.lexicalSelectedFontInfo;
    if (fontInfo) {
      if (fontInfo.isMixed) return fontInfo.max; // start from max when editing
      if (Number.isFinite(fontInfo.max)) return fontInfo.max;
    }

    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const selectedText = window.selectedTextInfo.text;
      const fontSizeMatch = selectedText.match(
        /<fontsize[^>]*style="font-size:\s*(\d+)px"[^>]*>/,
      );
      if (fontSizeMatch) return parseInt(fontSizeMatch[1], 10);
      const wrappedMatch = selectedText.match(
        /<fontsize[^>]*style="font-size:\s*(\d+)px"[^>]*>(.*?)<\/fontsize>/,
      );
      if (wrappedMatch) return parseInt(wrappedMatch[1], 10);
    }
    return fontSize;
  };

  // Always use MIN_FONT_SIZE as the minimum constraint
  const dynamicMinFontSize = MIN_FONT_SIZE;

  const handleTextChange = (e) => {
    updateRectangleProperties({ text: e.target.value });
  };

  const handleFontSizeChange = (e, skipVerification = false) => {
    const newFontSize = parseInt(e.target.value, 10);

    // Set flag to prevent selection change listeners from interfering
    isProgrammaticallyChangingFontSizeRef.current = true;

    // Immediately set the input value to prevent it from being cleared
    setFontSizeInputValue(newFontSize);

    // For Lexical editor, we need to restore selection first, then apply
    if (window.applyLexicalFontSizeWithRestore) {
      // Disable selection update listeners temporarily to prevent stale value reads
      if (skipVerification) {
        selectionUpdateListenersRef.current.enabled = false;
      }

      window.applyLexicalFontSizeWithRestore(`${newFontSize}px`);

      // If this is a programmatic change (arrow keys), skip DOM verification
      // and just trust the value we set. Only verify for manual input changes.
      if (skipVerification) {
        // For programmatic changes, just keep the value we set and clear flag after a delay
        setTimeout(() => {
          isProgrammaticallyChangingFontSizeRef.current = false;
          // Re-enable listeners after a delay to let Lexical events settle
          setTimeout(() => {
            selectionUpdateListenersRef.current.enabled = true;
          }, 100);
        }, 250);
        return;
      }

      // Only verify from DOM for manual input changes (not arrow keys)
      setTimeout(() => {
        const fontInfo = window.lexicalSelectedFontInfo;
        if (fontInfo && Number.isFinite(fontInfo.max)) {
          setFontSizeInputValue(fontInfo.max);
        } else {
          // Try DOM detection as fallback
          const domSize = getSelectedFontSizeFromDOM();
          if (Number.isFinite(domSize)) {
            setFontSizeInputValue(domSize);
          } else {
            // Keep the new font size value
            setFontSizeInputValue(newFontSize);
          }
        }
        // Clear the flag after update with a small additional delay to prevent race conditions
        setTimeout(() => {
          isProgrammaticallyChangingFontSizeRef.current = false;
        }, 50);
      }, 200);

      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedRectangle.text || "";

      // Apply font size formatting to selected text
      const newFormattedText = applyFontSizeToSelectedText(
        currentFormattedText,
        newFontSize,
      );

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
        // Clear the flag after update
        isProgrammaticallyChangingFontSizeRef.current = false;
      }, 0);
    } else {
      // Apply to entire text - update global font size
      updateRectangleProperties({ fontSize: newFontSize });
      // Clear the flag after update
      setTimeout(() => {
        isProgrammaticallyChangingFontSizeRef.current = false;
      }, 0);
    }
  };

  // Font size increment/decrement functions that preserve focus
  const handleFontSizeIncrement = (e) => {
    // Prevent default to stop any focus changes
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Set flag to prevent selection change listeners from interfering
    isProgrammaticallyChangingFontSizeRef.current = true;

    // Use Lexical font size if available - these work directly on selection
    if (window.applyLexicalFontSizeIncrement) {
      // Use fontSizeInputValue directly instead of getCurrentFontSize() to avoid reading stale values
      const currentSize = Number.isFinite(fontSizeInputValue)
        ? fontSizeInputValue
        : fontSize;
      const newSize = Math.min(currentSize + 1, MAX_FONT_SIZE);

      // Immediately update to prevent blank value
      setFontSizeInputValue(newSize);

      // Disable selection update listeners temporarily
      selectionUpdateListenersRef.current.enabled = false;

      window.applyLexicalFontSizeIncrement();

      // For programmatic changes (buttons), don't verify from DOM
      // Just trust the value we set to avoid flickering from stale DOM reads
      // Clear the flag after a delay to allow normal updates to resume
      setTimeout(() => {
        isProgrammaticallyChangingFontSizeRef.current = false;
        // Re-enable listeners after a delay to let Lexical events settle
        setTimeout(() => {
          selectionUpdateListenersRef.current.enabled = true;
        }, 100);
      }, 250);

      return;
    }

    // Fallback to manual increment
    const currentFontSize = getCurrentFontSize();
    const newFontSize = Math.min(currentFontSize + 1, MAX_FONT_SIZE);
    handleFontSizeChange({ target: { value: newFontSize.toString() } });
  };

  const handleFontSizeDecrement = (e) => {
    // Prevent default to stop any focus changes
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Set flag to prevent selection change listeners from interfering
    isProgrammaticallyChangingFontSizeRef.current = true;

    // Use Lexical font size if available - these work directly on selection
    if (window.applyLexicalFontSizeDecrement) {
      // Use fontSizeInputValue directly instead of getCurrentFontSize() to avoid reading stale values
      const currentSize = Number.isFinite(fontSizeInputValue)
        ? fontSizeInputValue
        : fontSize;
      const newSize = Math.max(currentSize - 1, MIN_FONT_SIZE);

      // Immediately update to prevent blank value
      setFontSizeInputValue(newSize);

      // Disable selection update listeners temporarily
      selectionUpdateListenersRef.current.enabled = false;

      window.applyLexicalFontSizeDecrement();

      // For programmatic changes (buttons), don't verify from DOM
      // Just trust the value we set to avoid flickering from stale DOM reads
      // Clear the flag after a delay to allow normal updates to resume
      setTimeout(() => {
        isProgrammaticallyChangingFontSizeRef.current = false;
        // Re-enable listeners after a delay to let Lexical events settle
        setTimeout(() => {
          selectionUpdateListenersRef.current.enabled = true;
        }, 100);
      }, 250);

      return;
    }

    // Fallback to manual decrement
    const currentFontSize = getCurrentFontSize();
    const newFontSize = Math.max(currentFontSize - 1, MIN_FONT_SIZE);
    handleFontSizeChange({ target: { value: newFontSize.toString() } });
  };

  // Helper to validate and parse hex color
  const validateAndParseHexColor = (hexValue) => {
    // Remove # if present and trim whitespace
    let hex = hexValue.trim().replace(/^#/, "").toUpperCase();

    // Validate hex: must be 3 or 6 characters, only 0-9 and A-F
    if (!/^[0-9A-F]{3}$|^[0-9A-F]{6}$/.test(hex)) {
      return null; // Invalid hex
    }

    // Convert 3-digit to 6-digit if needed
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    return `#${hex}`;
  };

  const handleTextColorChange = (e) => {
    const newColor = e.target.value;

    // Update local hex input to stay in sync
    setLocalInputValues((prev) => ({
      ...prev,
      textColorHex: newColor.toUpperCase().replace("#", ""),
    }));

    // Use Lexical text color if available
    if (window.applyLexicalTextColor) {
      window.applyLexicalTextColor(newColor);
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedRectangle.text || "";

      // Apply color formatting to selected text
      const newFormattedText = applyColorToSelectedText(
        currentFormattedText,
        newColor,
      );

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text - update global text color
      updateRectangleProperties({ textColor: newColor });
    }
  };

  // Function to apply color formatting to selected text
  const applyColorToSelectedText = (formattedText, color) => {
    if (!window.selectedTextInfo || !window.selectedTextInfo.text.length) {
      return formattedText;
    }

    const { text: selectedText, start, end } = window.selectedTextInfo;

    // Find the selected text in the formatted content
    const beforeSelection = formattedText.substring(0, start);
    const afterSelection = formattedText.substring(end);

    // Apply color formatting to the selected text
    const coloredText = `<color style="color: ${color};">${selectedText}</color>`;

    return beforeSelection + coloredText + afterSelection;
  };

  // Function to apply color to entire text (for color swatches)
  const applyColorToEntireText = (color) => {
    // Update local hex input to stay in sync
    setLocalInputValues((prev) => ({
      ...prev,
      textColorHex: color.toUpperCase().replace("#", ""),
    }));

    // Use Lexical text color if available
    if (window.applyLexicalTextColor) {
      window.applyLexicalTextColor(color);
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedRectangle.text || "";

      // Apply color formatting to selected text
      const newFormattedText = applyColorToSelectedText(
        currentFormattedText,
        color,
      );

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text - update global text color
      updateRectangleProperties({ textColor: color });
    }
  };

  // Function to apply font size formatting to selected text
  const applyFontSizeToSelectedText = (formattedText, fontSize) => {
    if (!window.selectedTextInfo || !window.selectedTextInfo.text.length) {
      return formattedText;
    }

    const { text: selectedText } = window.selectedTextInfo;

    // For the fallback custom tag system, we'll use a simple replacement approach
    // Since the Lexical editor will handle the actual formatting, this is mainly for backup
    const escapedSelectedText = selectedText.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&",
    );
    const regex = new RegExp(escapedSelectedText, "g");

    let replaced = false;
    const result = formattedText.replace(regex, (match) => {
      if (!replaced) {
        replaced = true;
        return `<fontsize style="font-size: ${fontSize}px;">${match}</fontsize>`;
      }
      return match;
    });

    return result;
  };

  const handleNumberInput = (field, value) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      updateRectangleProperties({ [field]: num });
    }
  };

  const handleEnterKey = (event, commitFn) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitFn();
    }
  };

  const commitPositionValue = (field, rawValue, min, max) => {
    let num = parseInt(rawValue, 10);
    if (rawValue === "" || Number.isNaN(num)) num = min;
    if (num < min) num = min;
    if (num > max) num = max;

    // CRITICAL FIX: For off-canvas rectangles, ensure position stays within bounds
    // This acts as a "wall" to prevent going off screen
    // For main canvas, allow negative x values
    const rectWidth = Math.max(MIN_WIDTH, Number(selectedRectangle?.width) || MIN_WIDTH);
    const rectHeight = Math.max(
      MIN_HEIGHT,
      Number(selectedRectangle?.height) || MIN_HEIGHT,
    );
    const rotationDeg = Number(selectedRectangle?.rotation) || 0;
    const currentX = Number(selectedRectangle?.x) || 0;
    const currentY = Number(selectedRectangle?.y) || 0;
    const desiredX = field === "x" ? num : currentX;
    const desiredY = field === "y" ? num : currentY;
    const clamped = clampPositionForRotationInStage(
      desiredX,
      desiredY,
      rectWidth,
      rectHeight,
      rotationDeg,
    );
    num = field === "x" ? clamped.x : clamped.y;
    if (field === "x" && isOffCanvas) {
      const strokeHalf = Math.max(
        0,
        Number(selectedRectangle?.strokeWidth ?? 2) || 0,
      ) / 2;
      const strictMinNodeX = EDITOR_EDGE_BUFFER_PX + strokeHalf;
      num = Math.max(strictMinNodeX, num);
    }
    if (field === "y") {
    }

    setLocalInputValues((prev) => ({
      ...prev,
      [field]: num.toString(),
    }));
    updateRectangleProperties({ [field]: num });
  };
  const rotationAwarePositionBounds = getRotationAwarePositionBounds();

  const commitWidthValue = (rawValue) => {
    let num = parseFloat(rawValue);
    if (rawValue === "" || Number.isNaN(num)) num = MIN_WIDTH;
    if (num < MIN_WIDTH) num = MIN_WIDTH;
    if (num > editableMaxWidthForInput) num = editableMaxWidthForInput;

    // DEBUG: Log width value commit
    console.log("DEBUG: Width value commit:", {
      rawValue: rawValue,
      parsedNum: parseFloat(rawValue),
      finalNum: num,
      editableMaxWidthForInput: editableMaxWidthForInput,
      maintainAspectRatio: maintainAspectRatio
    });

    handleWidthChange(num);

    if (!(maintainAspectRatio && selectedRectangle.height)) {
      setLocalInputValues((prev) => ({
        ...prev,
        width: num.toFixed(2),
      }));
    }
  };

  const commitHeightValue = (rawValue) => {
    let num = parseFloat(rawValue);
    if (rawValue === "" || Number.isNaN(num)) num = MIN_HEIGHT;
    if (num < MIN_HEIGHT) num = MIN_HEIGHT;
    if (num > editableMaxHeightForInput) num = editableMaxHeightForInput;

    handleHeightChange(num);

    if (!(maintainAspectRatio && selectedRectangle.width)) {
      setLocalInputValues((prev) => ({
        ...prev,
        height: num.toFixed(2),
      }));
    }
  };

  const commitFillOpacityValue = (rawValue) => {
    if (rawValue === "") {
      setLocalInputValues((prev) => ({
        ...prev,
        fillOpacity: "0",
      }));
      updateRectangleProperties({ fillOpacity: 0 });
      return;
    }

    let num = parseInt(rawValue, 10);
    if (Number.isNaN(num)) num = 0;
    num = Math.max(0, Math.min(100, num));

    setLocalInputValues((prev) => ({
      ...prev,
      fillOpacity: num.toString(),
    }));
    updateRectangleProperties({ fillOpacity: num / 100 });
  };

  const commitStrokeOpacityValue = (rawValue) => {
    if (rawValue === "") {
      setLocalInputValues((prev) => ({
        ...prev,
        strokeOpacity: "0",
      }));
      updateRectangleProperties({ strokeOpacity: 0 });
      return;
    }

    let num = parseInt(rawValue, 10);
    if (Number.isNaN(num)) num = 0;
    num = Math.max(0, Math.min(100, num));

    setLocalInputValues((prev) => ({
      ...prev,
      strokeOpacity: num.toString(),
    }));
    updateRectangleProperties({ strokeOpacity: num / 100 });
  };

  const commitStrokeWidthValue = (rawValue) => {
    let num = parseInt(rawValue, 10);
    if (rawValue === "" || Number.isNaN(num)) num = MIN_STROKE_WIDTH;
    if (num < MIN_STROKE_WIDTH) num = MIN_STROKE_WIDTH;
    if (num > MAX_STROKE_WIDTH) num = MAX_STROKE_WIDTH;

    setLocalInputValues((prev) => ({
      ...prev,
      strokeWidth: num.toString(),
    }));
    updateRectangleProperties({ strokeWidth: num });
  };

  const commitRotationValue = (rawValue) => {
    const sanitized = rawValue.replace(/°/g, "");
    finalizeRotationChange(sanitized);
  };

  const commitStrokeColorHexValue = (rawValue) => {
    const hexValue = rawValue.replace(/^Hex\s*/i, "");
    const validColor = validateAndParseHexColor(hexValue);

    if (validColor) {
      updateRectangleProperties({ stroke: validColor });
      setLocalInputValues((prev) => ({
        ...prev,
        strokeColorHex: validColor.toUpperCase().replace("#", ""),
      }));
    } else {
      const currentHex = rgbaToHex(strokeColor).toUpperCase().replace("#", "");
      setLocalInputValues((prev) => ({
        ...prev,
        strokeColorHex: currentHex,
      }));
    }
  };

  const commitTextColorHexValue = (rawValue) => {
    const hexValue = rawValue.replace(/^Hex\s*/i, "");
    const validColor = validateAndParseHexColor(hexValue);

    if (validColor) {
      handleTextColorChange({ target: { value: validColor } });
    } else {
      const currentHex = textColor.toUpperCase().replace("#", "");
      setLocalInputValues((prev) => ({
        ...prev,
        textColorHex: currentHex,
      }));
    }
  };

  const rotate = (direction) => {
    if (!selectedRectangle) return;

    const oldRotation = selectedRectangle.rotation || 0;
    let newRotation = oldRotation + direction;

    // Normalize to 0-360 range
    newRotation = newRotation % 360;
    if (newRotation < 0) {
      newRotation += 360;
    }

    // Update both the properties and local state
    updateRectangleProperties({ rotation: newRotation });
    setLocalInputValues((prev) => ({
      ...prev,
      rotation: newRotation,
    }));
  };

  const handleAspectRatioChange = (checked) => {
    setMaintainAspectRatio(checked);

    if (checked) {
      // Switching to square mode - calculate equivalent side length that preserves the area
      // Area of rectangle = width * height
      // Area of square = side²
      // So: side = √(width * height)
      // Alternative approaches:
      // - Average: (width + height) / 2 (preserves average size)
      // - Max: Math.max(width, height) (preserves largest dimension)
      // - Min: Math.min(width, height) (preserves smallest dimension)
      const equivalentSide = Math.round(
        Math.sqrt(selectedRectangle.width * selectedRectangle.height),
      );

      updateRectangleProperties({
        maintainAspectRatio: checked,
        width: equivalentSide,
        height: equivalentSide,
        // CRITICAL FIX: Preserve x and y to ensure transformer anchors update correctly
        x: selectedRectangle.x,
        y: selectedRectangle.y,
      });

      // Update local input values
      setLocalInputValues((prev) => ({
        ...prev,
        width: equivalentSide.toFixed(2),
        height: equivalentSide.toFixed(2),
      }));
    } else {
      // Switching to rectangle mode - just toggle the flag, preserve current dimensions
      updateRectangleProperties({
        maintainAspectRatio: checked,
        // CRITICAL FIX: Preserve x and y to ensure transformer anchors update correctly
        x: selectedRectangle.x,
        y: selectedRectangle.y,
      });
    }
  };

  const handleWidthChange = (newWidth) => {
    const maxWidth = editableMaxWidthForInput;
    const maxHeight = getRotationAwareMaxHeight();
    if (maintainAspectRatio && selectedRectangle.height) {
      // Calculate the aspect ratio from current dimensions
      const aspectRatio = selectedRectangle.width / selectedRectangle.height;
      let constrainedWidth = Math.max(MIN_WIDTH, Math.min(maxWidth, newWidth));
      let newHeight = parseFloat((constrainedWidth / aspectRatio).toFixed(2));
      if (newHeight > maxHeight) {
        newHeight = maxHeight;
        constrainedWidth = parseFloat((newHeight * aspectRatio).toFixed(2));
      }

      // Update local input values to keep them in sync
      setLocalInputValues((prev) => ({
        ...prev,
        width: constrainedWidth.toFixed(2),
        height: newHeight.toFixed(2),
      }));

      updateRectangleProperties({
        width: constrainedWidth,
        height: newHeight,
      });
    } else {
      const constrainedWidth = Math.max(MIN_WIDTH, Math.min(maxWidth, newWidth));
      updateRectangleProperties({ width: constrainedWidth });
    }
  };

  const handleHeightChange = (newHeight) => {
    const maxWidth = getRotationAwareMaxWidth();
    const maxHeight = editableMaxHeightForInput;
    if (maintainAspectRatio && selectedRectangle.width) {
      // Calculate the aspect ratio from current dimensions
      const aspectRatio = selectedRectangle.width / selectedRectangle.height;
      let constrainedHeight = Math.max(
        MIN_HEIGHT,
        Math.min(maxHeight, newHeight),
      );
      let newWidth = parseFloat((constrainedHeight * aspectRatio).toFixed(2));
      if (newWidth > maxWidth) {
        newWidth = maxWidth;
        constrainedHeight = parseFloat((newWidth / aspectRatio).toFixed(2));
      }

      // Update local input values to keep them in sync
      setLocalInputValues((prev) => ({
        ...prev,
        width: newWidth.toFixed(2),
        height: constrainedHeight.toFixed(2),
      }));

      updateRectangleProperties({
        width: newWidth,
        height: constrainedHeight,
      });
    } else {
      const constrainedHeight = Math.max(
        MIN_HEIGHT,
        Math.min(maxHeight, newHeight),
      );
      updateRectangleProperties({ height: constrainedHeight });
    }
  };

  const toggleHighlight = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalHighlight) {
      window.applyLexicalHighlight();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedRectangle.text || "";

      // Apply or remove highlight formatting using the helper
      const newFormattedText = toggleFormat("highlight", currentFormattedText);

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text - use the same approach as other formats
      const currentFormattedText = selectedRectangle.text || "";
      const newFormattedText = toggleFormat("highlight", currentFormattedText);

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);
    }
  };

  // Function to check if selected text or entire text has specific formatting
  const hasTextFormatting = (format) => {
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      // Check if selected text has the formatting
      const selectedText = window.selectedTextInfo.text;
      const regex = new RegExp(`<${format}>(.*?)<\/${format}>`, "g");
      return regex.test(selectedText);
    } else {
      // Check if entire text has the formatting
      const textContent = selectedRectangle.text || "";
      const regex = new RegExp(`<${format}>(.*?)<\/${format}>`, "g");
      return regex.test(textContent);
    }
  };

  // Get the current formatting states
  const isHighlighted = hasTextFormatting("highlight");
  const isBold = hasTextFormatting("bold");
  const isItalic = hasTextFormatting("italic");
  const isUnderlined = hasTextFormatting("underline");

  // Function to clean text from existing formatting tags
  const cleanTextFromTags = (text) => {
    return text
      .replace(/<highlight>(.*?)<\/highlight>/g, "$1")
      .replace(/<bold>(.*?)<\/bold>/g, "$1")
      .replace(/<italic>(.*?)<\/italic>/g, "$1")
      .replace(/<underline>(.*?)<\/underline>/g, "$1");
  };

  // Function to check if text has specific formatting
  const hasFormatting = (text, format) => {
    const regex = new RegExp(`<${format}>(.*?)<\/${format}>`, "g");
    return regex.test(text);
  };

  // Function to apply formatting while preserving existing formatting
  const applyFormatting = (text, format) => {
    // Check if the text contains this specific format anywhere
    const hasThisFormat = new RegExp(`<${format}>(.*?)<\/${format}>`, "g").test(
      text,
    );

    if (hasThisFormat) {
      // Remove this specific formatting while preserving others
      return text.replace(
        new RegExp(`<${format}>(.*?)<\/${format}>`, "g"),
        "$1",
      );
    } else {
      // Add this formatting around the text
      // Check if text already has any formatting tags
      const hasAnyFormatting = /<(bold|italic|underline|highlight)>/.test(text);

      if (hasAnyFormatting) {
        // If text already has formatting, we need to be more careful
        // We'll wrap the entire text with the new format, but this preserves existing formatting inside
        return `<${format}>${text}</${format}>`;
      } else {
        // If no existing formatting, simply wrap with the new format
        return `<${format}>${text}</${format}>`;
      }
    }
  };

  // Function to check if text has any formatting tags
  const hasAnyFormatting = (text) => {
    return /<(bold|italic|underline|highlight)>/.test(text);
  };

  // Helper function to safely replace text in formatted content
  const replaceTextInFormattedContent = (formattedText, oldText, newText) => {
    // Escape special regex characters in the old text
    const escapedOldText = oldText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedOldText, "g");
    return formattedText.replace(regex, newText);
  };

  // Helper function to safely apply or remove a specific format
  const toggleFormat = (format, currentText) => {
    const hasFormat = new RegExp(`<${format}>`).test(currentText);

    if (hasFormat) {
      // Remove the specific format while preserving others
      // Use a more sophisticated approach that handles nested tags properly
      let result = currentText;

      // First, find all opening and closing tags of this format
      const openingTag = `<${format}>`;
      const closingTag = `</${format}>`;

      // Find the first opening tag
      const firstOpenIndex = result.indexOf(openingTag);
      if (firstOpenIndex !== -1) {
        // Find the corresponding closing tag
        let depth = 0;
        let closingIndex = -1;

        for (let i = firstOpenIndex; i < result.length; i++) {
          if (result.substring(i, i + openingTag.length) === openingTag) {
            depth++;
          } else if (
            result.substring(i, i + closingTag.length) === closingTag
          ) {
            depth--;
            if (depth === 0) {
              closingIndex = i;
              break;
            }
          }
        }

        if (closingIndex !== -1) {
          // Remove the outermost pair of tags
          const beforeTags = result.substring(0, firstOpenIndex);
          const insideTags = result.substring(
            firstOpenIndex + openingTag.length,
            closingIndex,
          );
          const afterTags = result.substring(closingIndex + closingTag.length);
          result = beforeTags + insideTags + afterTags;
        }
      }

      return result;
    } else {
      // Add the format - wrap the entire text
      // This preserves any existing formatting inside
      const result = `<${format}>${currentText}</${format}>`;

      return result;
    }
  };

  const toggleBold = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalBold) {
      window.applyLexicalBold();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedRectangle.text || "";

      // Apply or remove bold formatting using the helper
      const newFormattedText = toggleFormat("bold", currentFormattedText);

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text
      const newFontStyle = fontStyle.includes("bold")
        ? fontStyle.replace("bold", "").trim()
        : (fontStyle + " bold").trim();
      updateRectangleProperties({ fontStyle: newFontStyle });
      setFormattingUpdate((prev) => prev + 1); // Trigger re-render for button states
    }
  };

  const toggleItalic = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalItalic) {
      window.applyLexicalItalic();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedRectangle.text || "";

      // Apply or remove italic formatting using the helper
      const newFormattedText = toggleFormat("italic", currentFormattedText);

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text
      const newFontStyle = fontStyle.includes("italic")
        ? fontStyle.replace("italic", "").trim()
        : (fontStyle + " italic").trim();
      updateRectangleProperties({ fontStyle: newFontStyle });
      setFormattingUpdate((prev) => prev + 1); // Trigger re-render for button states
    }
  };

  const toggleUnderline = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalUnderline) {
      window.applyLexicalUnderline();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedRectangle.text || "";

      // Apply or remove underline formatting using the helper
      const newFormattedText = toggleFormat("underline", currentFormattedText);

      updateRectangleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text
      const newTextDecoration = textDecoration.includes("underline")
        ? textDecoration.replace("underline", "").trim()
        : (textDecoration + " underline").trim();
      updateRectangleProperties({ textDecoration: newTextDecoration });
      setFormattingUpdate((prev) => prev + 1); // Trigger re-render for button states
    }
  };

  const saveCurrentStyleAsPreset = () => {
    if (editingPreset) return;
    const preset = {
      id: Date.now(),
      name: `Preset ${stylePresets.length + 1}`,
      fill,
      fillOpacity,
      stroke: selectedRectangle.stroke || strokeColor,
      strokeOpacity: selectedRectangle.strokeOpacity ?? 1,
      strokeWidth,
      fontSize,
      textColor,
      fontStyle,
      textDecoration,
      align,
      verticalAlign,
      text,
      width,
      height,
      rotation,
      maintainAspectRatio,
    };

    const updatedPresets = [...stylePresets, preset];
    setStylePresets(updatedPresets);

    // Immediately show the edit box for the newly created preset
    setEditingPreset(preset);
    setPendingDefaultPresetId(defaultPresetId);
    setDefaultPresetIdBeforeEdit(defaultPresetId);
    setPendingNewPresetId(preset.id);
  };

  const savePresetsToStorage = (presets) => {
    localStorage.setItem("rectangleStylePresets", JSON.stringify(presets));
  };

  const saveDefaultPresetToStorage = (preset) => {
    localStorage.setItem(DEFAULT_PRESET_STORAGE_KEY, JSON.stringify(preset));
    localStorage.setItem(DEFAULT_PRESET_ID_STORAGE_KEY, String(preset.id));
    setDefaultPresetId(String(preset.id));
  };

  useEffect(() => {
    if (!Array.isArray(stylePresets) || stylePresets.length === 0) {
      const fallback = createDefaultRectanglePreset();
      const next = [fallback];
      setStylePresets(next);
      savePresetsToStorage(next);
      saveDefaultPresetToStorage(fallback);
      return;
    }
    const storedId = localStorage.getItem(DEFAULT_PRESET_ID_STORAGE_KEY);
    const hasStoredDefault = stylePresets.some(
      (preset) => String(preset.id) === String(storedId),
    );
    if (!storedId || !hasStoredDefault) {
      saveDefaultPresetToStorage(stylePresets[0]);
    }
  }, [stylePresets]);

  const applyPreset = (preset) => {
    updateRectangleProperties({
      fill: preset.fill,
      fillColor: preset.fill,
      fillOpacity: preset.fillOpacity,
      stroke: preset.stroke || preset.strokeColor,
      strokeOpacity: preset.strokeOpacity,
      strokeWidth: preset.strokeWidth,
      fontSize: preset.fontSize,
      textColor: preset.textColor,
      fontStyle: preset.fontStyle,
      textDecoration: preset.textDecoration,
      align: preset.align,
      verticalAlign: preset.verticalAlign,
      text: preset.text,
      maintainAspectRatio: preset.maintainAspectRatio || false,
      isFirstTime: false,
    });
  };

  const rotationValue = localInputValues.rotation;
  const hasRotationValue =
    rotationValue !== "" &&
    rotationValue !== null &&
    rotationValue !== undefined;
  const rotationInputValue = isEditingRotation
    ? hasRotationValue
      ? String(rotationValue)
      : ""
    : hasRotationValue
      ? `${rotationValue}°`
      : "0°";

  return (
    <div>
      {!showTextEditor && (
        <>
          {/* Position */}
          <div
            style={{
              background: "#f5f5f5",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              display: "flex",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <label style={{ whiteSpace: "nowrap" }}>XPos</label>
              <input
                type="number"
                value={localInputValues.x}
                min={Math.floor(rotationAwarePositionBounds.minX)}
                max={Math.ceil(rotationAwarePositionBounds.maxX)}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    x: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  commitPositionValue(
                    "x",
                    value,
                    Math.floor(rotationAwarePositionBounds.minX),
                    Math.ceil(rotationAwarePositionBounds.maxX),
                  );
                }}
                onBlur={(e) =>
                  commitPositionValue(
                    "x",
                    e.target.value,
                    Math.floor(rotationAwarePositionBounds.minX),
                    Math.ceil(rotationAwarePositionBounds.maxX),
                  )
                }
                onKeyDown={(e) =>
                  handleEnterKey(e, () =>
                    commitPositionValue(
                      "x",
                      e.target.value,
                      Math.floor(rotationAwarePositionBounds.minX),
                      Math.ceil(rotationAwarePositionBounds.maxX),
                    ),
                  )
                }
                style={{
                  width: "60px",
                  padding: "4px 8px",
                  marginLeft: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <label style={{ whiteSpace: "nowrap" }}>YPos</label>
              <input
                type="number"
                value={localInputValues.y}
                min={Math.floor(rotationAwarePositionBounds.minY)}
                max={Math.ceil(rotationAwarePositionBounds.maxY)}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    y: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  commitPositionValue(
                    "y",
                    value,
                    Math.floor(rotationAwarePositionBounds.minY),
                    Math.ceil(rotationAwarePositionBounds.maxY),
                  );
                }}
                onBlur={(e) =>
                  commitPositionValue(
                    "y",
                    e.target.value,
                    Math.floor(rotationAwarePositionBounds.minY),
                    Math.ceil(rotationAwarePositionBounds.maxY),
                  )
                }
                onKeyDown={(e) =>
                  handleEnterKey(e, () =>
                    commitPositionValue(
                      "y",
                      e.target.value,
                      Math.floor(rotationAwarePositionBounds.minY),
                      Math.ceil(rotationAwarePositionBounds.maxY),
                    ),
                  )
                }
                style={{
                  width: "60px",
                  padding: "4px 8px",
                  marginLeft: "17px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                }}
              />
            </div>
          </div>

          {/* Size */}
          <div
            style={{
              background: "#f5f5f5",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ whiteSpace: "nowrap", minWidth: "50px" }}>
                Width
              </label>
              <input
                type="number"
                step="0.10"
                value={localInputValues.width}
                min={MIN_WIDTH}
                max={editableMaxWidthForInput}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  
                  // Only apply clamping on blur/enter, not on change
                  // This allows users to type huge values without immediate clamping
                  setLocalInputValues((prev) => ({
                    ...prev,
                    width: value,
                  }));
                }}
                onBlur={(e) => commitWidthValue(e.target.value)}
                onKeyDown={(e) =>
                  handleEnterKey(e, () => commitWidthValue(e.target.value))
                }
                style={{
                  flex: 1,
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ whiteSpace: "nowrap", minWidth: "50px" }}>
                Height
              </label>
              <input
                type="number"
                step="0.10"
                value={localInputValues.height}
                min={MIN_HEIGHT}
                max={editableMaxHeightForInput}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  
                  // Only apply clamping on blur/enter, not on change
                  // This allows users to type huge values without immediate clamping
                  setLocalInputValues((prev) => ({
                    ...prev,
                    height: value,
                  }));
                }}
                onBlur={(e) => commitHeightValue(e.target.value)}
                onKeyDown={(e) =>
                  handleEnterKey(e, () => commitHeightValue(e.target.value))
                }
                style={{
                  flex: 1,
                  padding: "4px 8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                }}
              />
            </div>
          </div>

          {/* Duplicate to Selected Location button */}
          {onToggleCloneMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                // Use setTimeout to ensure the toggle happens after event propagation is stopped
                setTimeout(() => {
                  onToggleCloneMode();
                }, 0);
              }}
              style={{
                width: "100%",
                padding: "10px 20px",
                backgroundColor: isCloneMode ? "#3B82F6" : "#F3F4F6",
                color: isCloneMode ? "white" : "#374151",
                border: isCloneMode ? "1px solid #3B82F6" : "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                marginBottom: "12px",
              }}
            >
              <CopyIcon
                width={16}
                height={19}
                fill={isCloneMode ? "white" : "#374151"}
              />
              <span>Duplicate to Selected Location</span>
            </button>
          )}

          {/* Aspect Ratio Checkbox */}
          <div
            style={{
              background: "#f5f5f5",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <input
              type="checkbox"
              id="maintainAspectRatio"
              checked={maintainAspectRatio}
              onChange={(e) => handleAspectRatioChange(e.target.checked)}
              style={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
              }}
            />
            <label
              htmlFor="maintainAspectRatio"
              style={{
                whiteSpace: "nowrap",
                fontSize: "14px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              Maintain Proportions (Square)
            </label>
          </div>

          <div
            style={{
              background: "#f5f5f5",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              display: "flex",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <label style={{ whiteSpace: "nowrap" }}>Rotate</label>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input
                type="text"
                value={rotationInputValue}
                onChange={(e) => {
                  const value = e.target.value;
                  // Remove degree symbol and only allow numbers
                  let numericValue = value.replace(/°/g, "");
                  // Trim leading zeros (but keep one zero if the value is just zeros)
                  if (numericValue.length > 1 && /^0+/.test(numericValue)) {
                    numericValue = numericValue.replace(/^0+/, "");
                  }
                  if (numericValue === "" || /^\d+$/.test(numericValue)) {
                    setLocalInputValues((prev) => ({
                      ...prev,
                      rotation: numericValue,
                    }));
                  }
                }}
                onFocus={() => {
                  setIsEditingRotation(true);
                }}
                onBlur={(e) => {
                  setIsEditingRotation(false);
                  commitRotationValue(e.target.value);
                }}
                onKeyDown={(e) =>
                  handleEnterKey(e, () => commitRotationValue(e.target.value))
                }
                style={{
                  width: "80px",
                  padding: "6px 8px",
                  border: "1px solid #ccc",
                  borderRight: "none",
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  fontSize: "14px",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startRotationChange(1);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    startRotationChange(1);
                  }}
                  onMouseUp={() => stopRotationChange(true)}
                  onMouseLeave={() => stopRotationChange(true)}
                  onTouchEnd={() => stopRotationChange(true)}
                  onTouchCancel={() => stopRotationChange(true)}
                  style={{
                    width: "20px",
                    height: "15px",
                    border: "1px solid #ccc",
                    borderTopRightRadius: "4px",
                    borderBottom: "none",
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "#6C757D",
                  }}
                  title="Hold to increase rotation"
                >
                  ▲
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    startRotationChange(-1);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    startRotationChange(-1);
                  }}
                  onMouseUp={() => stopRotationChange(true)}
                  onMouseLeave={() => stopRotationChange(true)}
                  onTouchEnd={() => stopRotationChange(true)}
                  onTouchCancel={() => stopRotationChange(true)}
                  style={{
                    width: "20px",
                    height: "15px",
                    border: "1px solid #ccc",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "#6C757D",
                  }}
                  title="Hold to decrease rotation"
                >
                  ▼
                </button>
              </div>
            </div>
          </div>

          {/* Style Section */}
          <div
            style={{
              background: "#f7f7f7",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                cursor: "pointer",
              }}
              onClick={() => setIsStyleSectionExpanded(!isStyleSectionExpanded)}
            >
              <h4
                style={{ margin: "0", fontWeight: "normal", fontSize: "14px" }}
              >
                Style
              </h4>
              <svg
                width="12"
                height="7"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isStyleSectionExpanded
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                  transition: "transform 0.2s",
                }}
              >
                <path
                  d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
                  fill="#6C757D"
                />
              </svg>
            </div>

            {isStyleSectionExpanded && (
              <>
                {/* Fill Color */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Fill Color
                  </label>
                  <input
                    type="color"
                    value={rgbaToHex(fill)}
                    onChange={(e) =>
                      updateRectangleProperties({
                        fill: e.target.value,
                        fillColor: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "4px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </div>

                {/* Fill Transparency */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Transparency
                  </label>
                  <div style={{ display: "flex", flex: 1 }}>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={localInputValues.fillOpacity}
                      onChange={(e) => {
                        const { value } = e.target;
                        const inputType = e.nativeEvent?.inputType;
                        setLocalInputValues((prev) => ({
                          ...prev,
                          fillOpacity: value,
                        }));
                        if (value === "" || shouldDeferInputUpdate(inputType)) {
                          return;
                        }
                        commitFillOpacityValue(value);
                      }}
                      onBlur={(e) => commitFillOpacityValue(e.target.value)}
                      onKeyDown={(e) =>
                        handleEnterKey(e, () =>
                          commitFillOpacityValue(e.target.value),
                        )
                      }
                      style={{
                        flex: 1,
                        padding: "4px",
                        border: "1px solid #ccc",
                        borderRight: "none",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                    />
                    <span
                      style={{
                        padding: "4px 8px",
                        border: "1px solid #ccc",
                        borderLeft: "none",
                        backgroundColor: "#f2f2f2",
                        borderTopRightRadius: "4px",
                        borderBottomRightRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      %
                    </span>
                  </div>
                </div>

                {/* Stroke Color */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Color
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <button
                      onClick={() => {
                        const newColor = "#dc3545";
                        updateRectangleProperties({ stroke: newColor });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: newColor
                            .toUpperCase()
                            .replace("#", ""),
                        }));
                      }}
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#dc3545",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        cursor: "pointer",
                      }}
                    />
                    <button
                      onClick={() => {
                        const newColor = "#fd7e14";
                        updateRectangleProperties({ stroke: newColor });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: newColor
                            .toUpperCase()
                            .replace("#", ""),
                        }));
                      }}
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#fd7e14",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        cursor: "pointer",
                      }}
                    />
                    <input
                      type="color"
                      value={rgbaToHex(strokeColor)}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        updateRectangleProperties({ stroke: newColor });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: newColor
                            .toUpperCase()
                            .replace("#", ""),
                        }));
                      }}
                      style={{
                        width: "40px",
                        height: "24px",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    />
                    <input
                      type="text"
                      value={`${localInputValues.strokeColorHex}`}
                      maxLength={10}
                      onChange={(e) => {
                        // Strip "Hex " prefix if present, then process hex value
                        let inputValue = e.target.value
                          .replace(/^Hex\s*/i, "")
                          .toUpperCase()
                          .replace("#", "");
                        // Limit to max 6 hex characters
                        if (inputValue.length > 6) {
                          inputValue = inputValue.slice(0, 6);
                        }
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: inputValue,
                        }));
                      }}
                      onBlur={(e) => commitStrokeColorHexValue(e.target.value)}
                      onKeyDown={(e) =>
                        handleEnterKey(e, () =>
                          commitStrokeColorHexValue(e.target.value),
                        )
                      }
                      placeholder="Hex 000000"
                      style={{
                        flex: 1,
                        padding: "4px 8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        fontSize: "12px",
                      }}
                    />
                  </div>
                </div>

                {/* Stroke Width */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Stroke Size
                  </label>
                  <input
                    type="number"
                    min={MIN_STROKE_WIDTH}
                    max={MAX_STROKE_WIDTH}
                    value={localInputValues.strokeWidth}
                    onChange={(e) => {
                      const { value } = e.target;
                      const inputType = e.nativeEvent?.inputType;
                      setLocalInputValues((prev) => ({
                        ...prev,
                        strokeWidth: value,
                      }));
                      if (value === "" || shouldDeferInputUpdate(inputType)) {
                        return;
                      }
                      commitStrokeWidthValue(value);
                    }}
                    onBlur={(e) => commitStrokeWidthValue(e.target.value)}
                    onKeyDown={(e) =>
                      handleEnterKey(e, () =>
                        commitStrokeWidthValue(e.target.value),
                      )
                    }
                    style={{
                      width: "100%",
                      padding: "4px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </div>

                {/* Stroke Transparency */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Transparency
                  </label>
                  <div style={{ display: "flex", flex: 1 }}>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={localInputValues.strokeOpacity}
                      onChange={(e) => {
                        const { value } = e.target;
                        const inputType = e.nativeEvent?.inputType;
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeOpacity: value,
                        }));
                        if (value === "" || shouldDeferInputUpdate(inputType)) {
                          return;
                        }
                        commitStrokeOpacityValue(value);
                      }}
                      onBlur={(e) => commitStrokeOpacityValue(e.target.value)}
                      onKeyDown={(e) =>
                        handleEnterKey(e, () =>
                          commitStrokeOpacityValue(e.target.value),
                        )
                      }
                      style={{
                        flex: 1,
                        padding: "6px",
                        border: "1px solid #ccc",
                        borderRight: "none",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                    />
                    <span
                      style={{
                        padding: "4px 8px",
                        border: "1px solid #ccc",
                        borderLeft: "none",
                        backgroundColor: "#f2f2f2",
                        borderTopRightRadius: "4px",
                        borderBottomRightRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      %
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    // gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={saveCurrentStyleAsPreset}
                    disabled={Boolean(editingPreset)}
                    style={{
                      background: "#f7f7f7",
                      border: "none",
                      width: "100%",
                      color: "#007bff",
                      fontSize: "13px",
                      opacity: editingPreset ? 0.5 : 1,
                      cursor: editingPreset ? "not-allowed" : "pointer",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        marginRight: "8px",
                      }}
                    >
                      +
                    </span>
                    Add this style To Preset
                  </button>
                </div>
              </>
            )}
          </div>

          {/* --- END NEW --- */}

          {/* Preset Controls */}
          <div
            style={{
              background: "#f7f7f7",
              borderRadius: "5px",
              marginTop: "-10px",
              padding: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setIsStylesExpanded(!isStylesExpanded)}
            >
              <h4 style={{ margin: "0", fontWeight: "normal" }}>Styles</h4>
              <svg
                width="12"
                height="7"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isStylesExpanded
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                  transition: "transform 0.2s",
                }}
              >
                <path
                  d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
                  fill="#6C757D"
                />
              </svg>
            </div>

            {isStylesExpanded && (
              <>
                {/* List Presets */}
                {stylePresets.map((preset) => (
                  <div key={preset.id}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                    <button
                      type="button"
                      onClick={() => {
                        if (editingPreset && editingPreset.id !== preset.id)
                          return;
                        applyPreset(preset);
                      }}
                      disabled={
                        Boolean(editingPreset) &&
                        editingPreset.id !== preset.id
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor:
                          editingPreset && editingPreset.id !== preset.id
                            ? "not-allowed"
                            : "pointer",
                        textAlign: "left",
                        flexGrow: 1,
                        padding: 0,
                        fontSize: "1rem",
                        color:
                          editingPreset && editingPreset.id !== preset.id
                            ? "#9ca3af"
                            : "inherit",
                        opacity:
                          editingPreset && editingPreset.id !== preset.id
                            ? 0.55
                            : 1,
                      }}
                    >
                      {preset.name}
                    </button>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        type="button"
                        disabled
                        title="Default is set in edit panel"
                        style={{
                          backgroundColor:
                            String(preset.id) === String(defaultPresetId)
                              ? "#2563eb"
                              : "#e5e7eb",
                          color:
                            String(preset.id) === String(defaultPresetId)
                              ? "white"
                              : "#111827",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "not-allowed",
                          padding: "2px 6px",
                          fontSize: "11px",
                          opacity: 0.45,
                        }}
                      >
                        Default
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (editingPreset) return;
                          setEditingPreset(preset);
                          setPendingDefaultPresetId(defaultPresetId);
                          setDefaultPresetIdBeforeEdit(defaultPresetId);
                        }}
                        disabled={Boolean(editingPreset)}
                        title="Edit"
                        style={{
                          backgroundColor: "#007bff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: editingPreset ? "not-allowed" : "pointer",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "22px",
                          height: "22px",
                          opacity: editingPreset ? 0.45 : 1,
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clipPath="url(#clip0_15909_16836)">
                            <path
                              d="M11.6263 1.45481C11.6964 1.5251 11.7357 1.6203 11.7357 1.71956C11.7357 1.81881 11.6964 1.91401 11.6263 1.98431L10.844 2.76731L9.34404 1.26731L10.1263 0.484307C10.1966 0.414005 10.292 0.374512 10.3914 0.374512C10.4908 0.374512 10.5862 0.414005 10.6565 0.484307L11.6263 1.45406V1.45481ZM10.3138 3.29681L8.81379 1.79681L3.70404 6.90731C3.66276 6.94858 3.63168 6.99891 3.61329 7.05431L3.00954 8.86481C2.99859 8.89781 2.99703 8.93321 3.00505 8.96705C3.01306 9.00089 3.03033 9.03184 3.05492 9.05643C3.07951 9.08102 3.11045 9.09828 3.14429 9.1063C3.17813 9.11431 3.21353 9.11276 3.24654 9.10181L5.05704 8.49806C5.11236 8.47988 5.16269 8.44906 5.20404 8.40806L10.3138 3.29681Z"
                              fill="white"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0.75 10.125C0.75 10.4234 0.868526 10.7095 1.0795 10.9205C1.29048 11.1315 1.57663 11.25 1.875 11.25H10.125C10.4234 11.25 10.7095 11.1315 10.9205 10.9205C11.1315 10.7095 11.25 10.4234 11.25 10.125V5.625C11.25 5.52554 11.2105 5.43016 11.1402 5.35984C11.0698 5.28951 10.9745 5.25 10.875 5.25C10.7755 5.25 10.6802 5.28951 10.6098 5.35984C10.5395 5.43016 10.5 5.52554 10.5 5.625V10.125C10.5 10.2245 10.4605 10.3198 10.3902 10.3902C10.3198 10.4605 10.2245 10.5 10.125 10.5H1.875C1.77554 10.5 1.68016 10.4605 1.60984 10.3902C1.53951 10.3198 1.5 10.2245 1.5 10.125V1.875C1.5 1.77554 1.53951 1.68016 1.60984 1.60984C1.68016 1.53951 1.77554 1.5 1.875 1.5H6.75C6.84946 1.5 6.94484 1.46049 7.01517 1.39017C7.08549 1.31984 7.125 1.22446 7.125 1.125C7.125 1.02554 7.08549 0.930161 7.01517 0.859835C6.94484 0.789509 6.84946 0.75 6.75 0.75H1.875C1.57663 0.75 1.29048 0.868526 1.0795 1.0795C0.868526 1.29048 0.75 1.57663 0.75 1.875V10.125Z"
                              fill="white"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_15909_16836">
                              <rect width="12" height="12" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (editingPreset || stylePresets.length <= 1) return;
                          const filtered = stylePresets.filter(
                            (p) => p.id !== preset.id,
                          );
                          setStylePresets(filtered);
                          savePresetsToStorage(filtered);
                          const storedDefaultId = localStorage.getItem(
                            DEFAULT_PRESET_ID_STORAGE_KEY,
                          );
                          if (
                            String(defaultPresetId) === String(preset.id) ||
                            (storedDefaultId != null &&
                              String(storedDefaultId) === String(preset.id))
                          ) {
                            saveDefaultPresetToStorage(filtered[0]);
                          }
                          // Clear editing state if the deleted preset is being edited
                          if (editingPreset && editingPreset.id === preset.id) {
                            setEditingPreset(null);
                            setPendingDefaultPresetId(null);
                            setDefaultPresetIdBeforeEdit(null);
                          }
                        }}
                        disabled={Boolean(editingPreset) || stylePresets.length <= 1}
                        title="Delete"
                        style={{
                          backgroundColor: "#dc3545",
                          border: "none",
                          borderRadius: "4px",
                          cursor:
                            editingPreset || stylePresets.length <= 1
                              ? "not-allowed"
                              : "pointer",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "22px",
                          height: "22px",
                          opacity:
                            editingPreset || stylePresets.length <= 1 ? 0.45 : 1,
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect width="18" height="18" rx="4" fill="#DC3545" />
                          <path
                            d="M5.875 14.5C5.53125 14.5 5.23698 14.3803 4.99219 14.141C4.7474 13.9016 4.625 13.6139 4.625 13.2778V5.33333H4V4.11111H7.125V3.5H10.875V4.11111H14V5.33333H13.375V13.2778C13.375 13.6139 13.2526 13.9016 13.0078 14.141C12.763 14.3803 12.4688 14.5 12.125 14.5H5.875ZM7.125 12.0556H8.375V6.55556H7.125V12.0556ZM9.625 12.0556H10.875V6.55556H9.625V12.0556Z"
                            fill="white"
                          />
                        </svg>
                      </button>
                    </div>
                    </div>
                    {editingPreset && editingPreset.id === preset.id && (
                      <div
                        style={{
                          marginTop: "8px",
                          marginBottom: "12px",
                          padding: "12px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                        }}
                      >
                        <h4>Edit Preset: {editingPreset.name}</h4>
                        <input
                          type="text"
                          value={editingPreset.name}
                          onChange={(e) =>
                            setEditingPreset({
                              ...editingPreset,
                              name: e.target.value,
                            })
                          }
                          placeholder="Preset name"
                          style={{
                            width: "100%",
                            padding: "6px",
                            border: "1px solid #ccc",
                            marginBottom: "12px",
                          }}
                        />
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "12px",
                            fontSize: "13px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              String(editingPreset.id) ===
                              String(pendingDefaultPresetId ?? defaultPresetId)
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPendingDefaultPresetId(
                                  String(editingPreset.id),
                                );
                              }
                            }}
                          />
                          Set as default preset
                        </label>
                        <button
                          onClick={() => {
                            const trimmedName = editingPreset.name.trim();
                            const isDuplicate = stylePresets.some(
                              (p) =>
                                p.id !== editingPreset.id &&
                                p.name.trim().toLowerCase() ===
                                  trimmedName.toLowerCase(),
                            );
                            if (isDuplicate) {
                              alert(
                                "A preset with this name already exists. Please choose a different name.",
                              );
                              return;
                            }
                            const updated = stylePresets.map((p) =>
                              p.id === editingPreset.id
                                ? { ...editingPreset, name: trimmedName }
                                : p,
                            );
                            const nextDefaultPresetId =
                              pendingDefaultPresetId ?? defaultPresetId;
                            const nextDefaultPreset = updated.find(
                              (p) =>
                                String(p.id) === String(nextDefaultPresetId),
                            );
                            setStylePresets(updated);
                            savePresetsToStorage(updated);
                            if (nextDefaultPreset) {
                              saveDefaultPresetToStorage(nextDefaultPreset);
                            }
                            setEditingPreset(null);
                            setPendingDefaultPresetId(null);
                            setDefaultPresetIdBeforeEdit(null);
                            setPendingNewPresetId(null);
                          }}
                          style={{
                            backgroundColor: "#2563eb",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "8px",
                          }}
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            const rolledBack = pendingNewPresetId
                              ? stylePresets.filter(
                                  (p) => p.id !== pendingNewPresetId,
                                )
                              : stylePresets;
                            if (pendingNewPresetId) {
                              setStylePresets(rolledBack);
                            }
                            const fallbackDefaultId =
                              defaultPresetIdBeforeEdit ?? defaultPresetId;
                            const restoredDefaultPreset =
                              rolledBack.find(
                                (p) =>
                                  String(p.id) === String(fallbackDefaultId),
                              ) || rolledBack[0];
                            if (restoredDefaultPreset) {
                              saveDefaultPresetToStorage(restoredDefaultPreset);
                            }
                            setEditingPreset(null);
                            setPendingDefaultPresetId(null);
                            setDefaultPresetIdBeforeEdit(null);
                            setPendingNewPresetId(null);
                          }}
                          style={{
                            backgroundColor: "#e5e7eb",
                            color: "#111827",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div
            style={{
              marginTop: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <button
              onClick={() => {
                if (selectedRectangle.text) {
                  const willOpen = !showTextEditor;
                  // Set changingText flag BEFORE toggling editor to prevent any intermediate saves
                  if (willOpen) {
                    // Opening text editor - set changingText: true FIRST to prevent history saves
                    // Use a flag to prevent any intermediate updates from setting pendingChanges
                    updateRectangleProperties({ changingText: true });
                    // Immediately reset pendingChanges to ensure it's false
                    // This prevents race conditions where other updates might set it to true
                    setTimeout(() => {
                      updateRectangleProperties({ changingText: true }); // Set again to ensure flag is set
                      setShowTextEditor(true);
                    }, 0);
                  } else {
                    // Closing text editor - close first, then set changingText: false
                    setShowTextEditor(false);
                    // Set changingText: false to allow history save if there were changes
                    setTimeout(() => {
                      updateRectangleProperties({ changingText: false });
                    }, 0);
                  }
                } else {
                  // Adding text for the first time - onAddText will save to history (expected)
                  onAddText(selectedRectangle.id);
                  setShowTextEditor(true);
                  // Set changingText: true after adding text to prevent additional saves
                  setTimeout(() => {
                    updateRectangleProperties({ changingText: true });
                  }, 100); // Delay to ensure onAddText's save completes first
                }
              }}
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px solid gray",
                backgroundColor: selectedRectangle.text ? "white" : "#6c757e",
                color: selectedRectangle.text ? "#000" : "#fff",
              }}
            >
              {selectedRectangle.text
                ? "Edit Text in Shape"
                : "Add Text to Shape"}
            </button>

            <button
              onClick={onDelete}
              style={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px solid red",
                color: "red",
                marginTop: "10px",
              }}
            >
              Delete Rectangle
            </button>
          </div>
        </>
      )}

      {showTextEditor && (
        <>
          {/* Back Navigation */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "16px",
              cursor: "pointer",
            }}
            onClick={() => {
              setShowTextEditor(false);
              // Set changingText: false when closing text editor to allow history save if there were changes
              updateRectangleProperties({ changingText: false });
            }}
          >
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17 7.99998C17 7.71822 16.8881 7.44799 16.6888 7.24876C16.4896 7.04952 16.2194 6.9376 15.9376 6.9376H3.62887L8.19073 2.37786C8.28951 2.27908 8.36786 2.16182 8.42132 2.03276C8.47478 1.9037 8.50229 1.76538 8.50229 1.62569C8.50229 1.486 8.47478 1.34768 8.42132 1.21862C8.36786 1.08956 8.28951 0.972299 8.19073 0.873523C8.09196 0.774748 7.9747 0.696394 7.84564 0.642937C7.71658 0.589479 7.57826 0.561967 7.43857 0.561967C7.29888 0.561967 7.16056 0.589479 7.0315 0.642937C6.90244 0.696394 6.78518 0.774748 6.6864 0.873523L0.312114 7.24781C0.213179 7.3465 0.134683 7.46373 0.0811257 7.5928C0.027568 7.72187 0 7.86024 0 7.99998C0 8.13972 0.027568 8.27808 0.0811257 8.40715C0.134683 8.53622 0.213179 8.65346 0.312114 8.75214L6.6864 15.1264C6.88589 15.3259 7.15645 15.438 7.43857 15.438C7.72069 15.438 7.99125 15.3259 8.19073 15.1264C8.39022 14.9269 8.50229 14.6564 8.50229 14.3743C8.50229 14.0921 8.39022 13.8216 8.19073 13.6221L3.62887 9.06236H15.9376C16.2194 9.06236 16.4896 8.95043 16.6888 8.75119C16.8881 8.55196 17 8.28174 17 7.99998Z"
                fill="#6C757D"
              />
            </svg>

            <span style={{ color: "#000", fontSize: "14px" }}>
              Edit Text in Shape
            </span>
          </div>

          {/* Text Alignment Controls */}
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              gap: "12px",
              width: "80%%",
            }}
          >
            {/* Horizontal Alignment Container */}
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                padding: "4px",
                display: "flex",
                gap: "2px",
                border: "1px solid #e9ecef",
                flex: 1,
              }}
            >
              {/* Left Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateRectangleProperties({ align: "left" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor: align === "left" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "3px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: align === "left" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 20V0H2V20H0ZM4 15V12H14V15H4ZM4 8V5H20V8H4Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Center Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateRectangleProperties({ align: "center" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor:
                    align === "center" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "3px",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: align === "center" ? "white" : "#495057",
                }}
              >
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 20V15H3V12H8V8H0V5H8V0H10V5H18V8H10V12H15V15H10V20H8Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Right Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateRectangleProperties({ align: "right" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor:
                    align === "right" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "3px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: align === "right" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 20V0H20V20H18ZM6 15V12H16V15H6ZM0 8V5H16V8H0Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>
            </div>

            {/* Vertical Alignment Container */}
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                padding: "4px",
                display: "flex",
                gap: "2px",
                border: "1px solid #e9ecef",
                flex: 1,
              }}
            >
              {/* Top Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateRectangleProperties({ verticalAlign: "top" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor:
                    verticalAlign === "top" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "3px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: verticalAlign === "top" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 20V4H8V20H5ZM12 14V4H15V14H12ZM0 2V0H20V2H0Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Middle Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateRectangleProperties({ verticalAlign: "middle" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor:
                    verticalAlign === "middle" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  marginLeft: "3px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: verticalAlign === "middle" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 18V10H0V8H5V0H8V8H12V3H15V8H20V10H15V15H12V10H8V18H5Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Bottom Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateRectangleProperties({ verticalAlign: "bottom" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor:
                    verticalAlign === "bottom" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "3px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: verticalAlign === "bottom" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 20V18H20V20H0ZM5 16V0H8V16H5ZM12 16V6H15V16H12Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Text Styling Buttons */}
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              gap: "8px",
              width: "100%",
            }}
          >
            <button
              onClick={toggleHighlight}
              title={isHighlighted ? "Remove Highlight" : "Add Highlight"}
              style={{
                flex: 1,
                height: "35px",
                backgroundColor: isHighlighted ? "#e1e6ea" : "#f8f9fa",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: isHighlighted ? "white" : "#6C757D",
                transition: "all 0.2s ease",
              }}
            >
              <svg
                width="100%"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.1875 16V13.7554H15.1516V16H0.1875ZM9.39192 7.12946L7.44659 5.18413L4.49679 8.13374C4.45364 8.17689 4.43207 8.22964 4.43207 8.29199C4.43207 8.35434 4.45364 8.40715 4.49679 8.45042L6.11404 10.0791C6.15731 10.1222 6.21012 10.1438 6.27247 10.1438C6.33482 10.1438 6.38757 10.1222 6.43071 10.0791L9.39192 7.12946ZM8.24642 4.39571L10.1803 6.32944L13.3746 3.14676C13.4178 3.10362 13.4393 3.04844 13.4393 2.98123C13.4393 2.91401 13.4178 2.85883 13.3746 2.81568L11.7602 1.20143C11.717 1.15828 11.6619 1.13671 11.5948 1.13671C11.5276 1.13671 11.4724 1.15828 11.4293 1.20143L8.24642 4.39571ZM7.06369 3.98999L10.5861 7.51217L7.23073 10.8791C6.96013 11.1496 6.64258 11.2848 6.27808 11.2848C5.91358 11.2848 5.59609 11.1496 5.32561 10.8791L5.2536 10.8071L4.5688 11.446H1.6624L3.78899 9.33087L3.73137 9.27345C3.4609 9.00284 3.3218 8.68149 3.31406 8.30938C3.30646 7.93715 3.43789 7.6158 3.70837 7.34532L7.06369 3.98999ZM7.06369 3.98999L10.648 0.405715C10.9185 0.135238 11.2359 0 11.6004 0C11.9649 0 12.2825 0.135238 12.5531 0.405715L14.1817 2.02296C14.4522 2.29344 14.5875 2.61093 14.5875 2.97543C14.5875 3.33993 14.4522 3.65742 14.1817 3.92789L10.5861 7.51217L7.06369 3.98999Z"
                  fill={isHighlighted ? "white" : "#6C757D"}
                />
              </svg>
            </button>
            <button
              onClick={toggleBold}
              style={{
                flex: 1,
                height: "35px",
                backgroundColor: isBold ? "#e1e6ea" : "#f8f9fa",
                border: "none",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: isBold ? "white" : "#000",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              <svg
                width="100%"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.804688 14V0H6.32969C7.41302 0 8.41302 0.333333 9.32969 1C10.2464 1.66667 10.7047 2.59167 10.7047 3.775C10.7047 4.625 10.513 5.27917 10.1297 5.7375C9.74636 6.19583 9.38802 6.525 9.05469 6.725C9.47135 6.90833 9.93386 7.25 10.4422 7.75C10.9505 8.25 11.2047 9 11.2047 10C11.2047 11.4833 10.663 12.5208 9.57969 13.1125C8.49636 13.7042 7.47969 14 6.52969 14H0.804688ZM3.82969 11.2H6.42969C7.22969 11.2 7.71719 10.9958 7.89219 10.5875C8.06719 10.1792 8.15469 9.88333 8.15469 9.7C8.15469 9.51667 8.06719 9.22083 7.89219 8.8125C7.71719 8.40417 7.20469 8.2 6.35469 8.2H3.82969V11.2ZM3.82969 5.5H6.15469C6.70469 5.5 7.10469 5.35833 7.35469 5.075C7.60469 4.79167 7.72969 4.475 7.72969 4.125C7.72969 3.725 7.58802 3.4 7.30469 3.15C7.02135 2.9 6.65469 2.775 6.20469 2.775H3.82969V5.5Z"
                  fill={isBold ? "#6C757D" : "#6C757D"}
                />
              </svg>
            </button>
            <button
              onClick={toggleItalic}
              style={{
                flex: 1,
                height: "35px",
                backgroundColor: isItalic ? "#e1e6ea" : "#f8f9fa",
                border: "none",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: isItalic ? "white" : "#000",
                fontStyle: "italic",
                fontSize: "14px",
              }}
            >
              <svg
                width="100%"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.828125 14V11.5H4.82812L7.82812 2.5H3.82812V0H13.8281V2.5H10.3281L7.32812 11.5H10.8281V14H0.828125Z"
                  fill={isItalic ? "#6C757D" : "#6C757D"}
                />
              </svg>
            </button>
          </div>

          {/* Color Selection */}
          <div
            style={{
              marginTop: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {/* Color Swatches */}
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#007bff")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#007bff",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#FF0000")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#FF0000",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#28a745")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#28a745",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#212529")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#212529",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
            </div>

            {/* Color Picker */}
            <input
              type="color"
              value={textColor}
              onChange={handleTextColorChange}
              onFocus={() => {
                // Save the current Lexical selection before focusing color picker
                if (window.saveLexicalSelection) {
                  window.saveLexicalSelection();
                }
              }}
              style={{
                width: "40px",
                height: "24px",
                border: "1px solid #ccc",
                borderRadius: "2px",
                cursor: "pointer",
                padding: 0,
              }}
            />

            {/* Hex Input */}
            <input
              type="text"
              value={`Hex ${localInputValues.textColorHex}`}
              maxLength={10}
              onChange={(e) => {
                // Strip "Hex " prefix if present, then process hex value
                let inputValue = e.target.value
                  .replace(/^Hex\s*/i, "")
                  .toUpperCase()
                  .replace("#", "");
                // Limit to max 6 hex characters
                if (inputValue.length > 6) {
                  inputValue = inputValue.slice(0, 6);
                }
                setLocalInputValues((prev) => ({
                  ...prev,
                  textColorHex: inputValue,
                }));
              }}
              onBlur={(e) => commitTextColorHexValue(e.target.value)}
              onKeyDown={(e) =>
                handleEnterKey(e, () => commitTextColorHexValue(e.target.value))
              }
              onFocus={() => {
                // Clear any previous highlights first
                if (window.clearLexicalSelectionHighlight) {
                  window.clearLexicalSelectionHighlight();
                }

                // Save the current Lexical selection before focusing input
                if (window.saveLexicalSelection) {
                  window.saveLexicalSelection();
                }
              }}
              data-color-input="true"
              placeholder="Hex 000000"
              style={{
                flex: 1,
                padding: "6px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "white",
                fontSize: "12px",
              }}
            />
          </div>

          {/* Font Size Control */}
          <div style={{ marginTop: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Font Size:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Only prevent on buttons to avoid stealing focus
                  handleFontSizeDecrement(e);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  border: "1px solid #ccc",
                  borderTopLeftRadius: "4px",
                  borderBottomLeftRadius: "4px",
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#6C757D",
                }}
                title="Decrease font size"
              >
                -
              </button>

              <input
                key={`font-size-${formattingUpdate}`}
                type="number"
                value={fontSizeInputValue}
                min={dynamicMinFontSize}
                max={MAX_FONT_SIZE}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value, 10);
                  setFontSizeInputValue(
                    Number.isFinite(newValue) ? newValue : "",
                  );
                  // Don't apply changes on every keystroke to preserve selection
                }}
                onKeyDown={(e) => {
                  // Handle arrow keys to apply font size changes immediately
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    // Prevent default to avoid the input from taking focus
                    e.preventDefault();

                    // Set flag FIRST to prevent any interference
                    isProgrammaticallyChangingFontSizeRef.current = true;

                    // Calculate new font size - use the input value directly, not getCurrentFontSize()
                    // to avoid reading stale values from window.lexicalSelectedFontInfo
                    const currentValue = parseInt(e.target.value, 10);
                    if (!Number.isFinite(currentValue)) {
                      // If input is empty/invalid, use fontSize prop as fallback
                      const fallbackValue = fontSize || 14;
                      const newFontSize =
                        e.key === "ArrowUp"
                          ? fallbackValue + 1
                          : fallbackValue - 1;
                      setFontSizeInputValue(newFontSize);
                      handleFontSizeChange(
                        {
                          target: { value: newFontSize.toString() },
                        },
                        true,
                      );
                      return;
                    }
                    const newFontSize =
                      e.key === "ArrowUp" ? currentValue + 1 : currentValue - 1;

                    // Update the input value
                    setFontSizeInputValue(newFontSize);

                    // Apply the font size change immediately, skipping DOM verification
                    // to prevent reading stale values that cause flickering
                    handleFontSizeChange(
                      {
                        target: { value: newFontSize.toString() },
                      },
                      true,
                    ); // true = skip verification
                  } else if (e.key === "Enter") {
                    // Apply on Enter key - selection restoration handled in handleFontSizeChange
                    e.preventDefault();
                    if (
                      e.target.value !== "" &&
                      !isNaN(parseInt(e.target.value, 10))
                    ) {
                      handleFontSizeChange(e);
                    }
                  }
                }}
                onBlur={(e) => {
                  // Apply font size change when user finishes editing
                  // Selection restoration is now handled inside handleFontSizeChange
                  if (
                    e.target.value !== "" &&
                    !isNaN(parseInt(e.target.value, 10))
                  ) {
                    handleFontSizeChange(e);
                  }
                }}
                onFocus={() => {
                  // Clear any previous highlights first
                  if (window.clearLexicalSelectionHighlight) {
                    window.clearLexicalSelectionHighlight();
                  }

                  setFontSizeInputValue(getCurrentFontSize());

                  // Save the current Lexical selection before focusing input
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                data-font-size-input="true"
                className="font-size-input"
                style={{
                  border: "1px solid #ccc",
                  borderLeft: "none",
                  borderRight: "none",
                }}
              />

              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Only prevent on buttons to avoid stealing focus
                  handleFontSizeIncrement(e);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  border: "1px solid #ccc",
                  borderTopRightRadius: "4px",
                  borderBottomRightRadius: "4px",
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#6C757D",
                }}
                title="Increase font size"
              >
                +
              </button>
            </div>
          </div>

          {/* Text Input Area */}
          <div style={{ marginTop: "16px" }}>
            <SimpleLexicalEditorWrapper
              value={textContent}
              onChange={(content) => {
                updateRectangleProperties({
                  text: content,
                  changingText: true,
                });
              }}
              onBlur={() => {
                updateRectangleProperties({
                  changingText: false,
                });
              }}
              placeholder="Enter text..."
              autoFocus={true}
              style={{
                height: "100px",
                fontFamily: "Arial, sans-serif",
                fontSize: `${fontSize}px`,
                fontWeight: fontStyle.includes("bold") ? "bold" : "normal",
                fontStyle: fontStyle.includes("italic") ? "italic" : "normal",
                textDecoration: textDecoration,
                color: textColor,
                textAlign: align,
                lineHeight: "1.2",
              }}
            />
          </div>

          {/* Text Alignment Controls */}

          {/* Remove Text */}
          <div style={{ marginTop: "24px" }}>
            <button
              onClick={() => {
                onRemoveText(selectedRectangle.id);
                setShowTextEditor(false);
              }}
              style={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px solid red",
                color: "red",
                marginTop: "10px",
              }}
            >
              Remove Text
            </button>
          </div>
        </>
      )}
    </div>
  );
}

RectangleEditor.propTypes = {
  selectedRectangle: PropTypes.object.isRequired,
  updateRectangleProperties: PropTypes.func.isRequired,
  onAddText: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRemoveText: PropTypes.func.isRequired,
};

export default RectangleEditor;
